import {
  getSessionToken,
  isTokenExpired,
  refreshSession,
} from "@/lib/auth/token-manager";
import type { Plan } from "@headerhawk/core";
import type { SourceFlag, SourceIdentity } from "@headerhawk/core/csp";
import type { ReportType } from "@headerhawk/core/report-types";
import { explorerFilterParams, withQuery } from "./query-params";
import { DESCOPE_PROJECT } from "@/lib/auth/descope-project";
import { isDemoTab } from "@/lib/auth/impersonation";
import { DEMO_API_URL } from "@/lib/demo/start";

// Mock auth helpers for dev mode
function isMockAuthEnabled(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_MOCK_AUTH === "true";
}

function getMockSessionToken(): string {
  return "mock-dev-token-not-for-production";
}

// A demo tab (task 405) talks to the demo stage's host and nothing else: the
// customer stage refuses a demo session, and the demo stage refuses every
// other. Fixed for the life of the page, since boot decides the tab's kind.
const API_URL = isDemoTab() ? DEMO_API_URL : import.meta.env.VITE_API_URL || "";

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    /** The body's `details`, when it has one (a refused host list names its entries there). */
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Get a valid authentication token, refreshing if necessary.
 *
 * This handles the "stale tab" problem where a user leaves the dashboard open
 * for a long time. When they return, the token may be expired. Descope's
 * autoRefresh runs in the background but may not have completed by the time
 * API calls are made (especially if the browser throttled timers while the
 * tab was backgrounded).
 *
 * This function proactively checks for expiration and refreshes before making
 * requests, avoiding 401 errors that would otherwise require a full page reload.
 */
async function getValidToken(): Promise<string | undefined> {
  if (isMockAuthEnabled()) {
    return getMockSessionToken();
  }

  const token = getSessionToken();

  // No token at all - user needs to log in
  if (!token) {
    return undefined;
  }

  // Token exists and is still valid
  if (!isTokenExpired(token)) {
    return token;
  }

  // Token is expired - try to refresh it
  try {
    const response = await refreshSession(DESCOPE_PROJECT);
    if (response.ok && response.sessionJwt) {
      return response.sessionJwt;
    }
    // Refresh failed - return undefined so caller can handle appropriately
    return undefined;
  } catch {
    // Refresh threw an error - return undefined
    return undefined;
  }
}

/**
 * Send an authenticated API request and return the response, having thrown
 * `ApiError` for anything but a 2xx.
 *
 * Handles token refresh in two scenarios:
 * 1. Proactively: Checks if token is expired before making request
 * 2. Reactively: On 401 response, refreshes token and retries once
 *
 * This ensures resilience against stale tokens from backgrounded tabs.
 */
async function sendWithAuth(
  path: string,
  options: RequestInit = {},
  isRetry = false,
): Promise<Response> {
  const token = await getValidToken();

  if (!token) {
    // No session-expired signal here: when a refresh actually failed the token
    // layer has already dispatched one, and when there simply is no token
    // RequireAuth's unauthenticated gate covers it.
    throw new ApiError("No authentication token", 401, "NO_TOKEN");
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  // Handle 401 with retry-after-refresh (only once to avoid loops).
  //
  // A 401 here is NOT proof the session is over. A freshly signed-up user hits
  // one from every endpoint until provisioning creates their tenant, and
  // treating that as expiry threw them back to the login page mid-signup. Only
  // the refresh call can say the credentials are dead, and `refreshSession`
  // raises the session-expired signal itself when they are.
  if (response.status === 401 && !isRetry) {
    try {
      const refreshResponse = await refreshSession(DESCOPE_PROJECT);
      if (refreshResponse.ok && refreshResponse.sessionJwt) {
        // Retry the original request with the fresh token
        return sendWithAuth(path, options, true);
      }
    } catch {
      // Refresh failed - fall through to throw the 401 error
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || "Request failed",
      response.status,
      errorData.error,
      typeof errorData.details === "object" && errorData.details !== null
        ? (errorData.details as Record<string, unknown>)
        : undefined,
    );
  }

  return response;
}

/** Make an authenticated API request and read its JSON body. */
async function fetchWithAuth<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await sendWithAuth(path, options);

  if (response.status === 204) {
    return null as T;
  }

  return response.json() as Promise<T>;
}

/** Make an authenticated API request and read its body as text. */
async function fetchTextWithAuth(path: string): Promise<string> {
  return (await sendWithAuth(path)).text();
}

export interface Site {
  id: string;
  name: string;
  /**
   * The hosts this site accepts reports from, normalised: exact hosts and
   * `*.` wildcards (task 303). Never empty.
   */
  hosts: string[];
  /**
   * The code that names the site's report host, `https://{code}.ingest…/r`
   * (12 characters since task 417). Only present on the response that created
   * the site; `toSiteResponse` withholds it elsewhere.
   */
  ingestCode?: string;
  /**
   * The report host label the tenant chose for this site,
   * `https://{ingestLabel}.ingest…/r`, or null (task 421).
   */
  ingestLabel: string | null;
  isActive: boolean;
  /**
   * The switch and sample rate of every report type the dashboard offers, in
   * registry order (task 309). `csp-hash` is the script hash rate.
   */
  reportTypes: SiteReportTypeSetting[];
  /**
   * When this site's first CSP violation report arrived, or null if it never
   * has. Stamped once and never moved, so it survives report retention — the
   * signal for an onboarding "waiting for your first report" state (task 185;
   * task 176 owns that UI).
   */
  firstReportAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SiteReportTypeSetting {
  type: ReportType;
  enabled: boolean;
  /** 0.01 to 1. Never 0: a type that keeps nothing is `enabled: false`. */
  sampleRate: number;
  configurable: { enabled: boolean; sampleRate: boolean };
}

/** A change to one type's settings, either field. */
export type SiteReportTypeSettingPatch = Partial<
  Pick<SiteReportTypeSetting, "enabled" | "sampleRate">
>;

export type CspDirective =
  | "default-src"
  | "script-src"
  | "style-src"
  | "img-src"
  | "font-src"
  | "connect-src"
  | "media-src"
  | "object-src"
  | "frame-src"
  | "child-src"
  | "worker-src"
  | "frame-ancestors"
  | "form-action"
  | "base-uri"
  | "manifest-src"
  | "require-trusted-types-for"
  | "trusted-types";

export interface Report {
  id: string;
  tenantId: string;
  siteId: string;
  receivedAt: string;
  occurredAt?: string;
  documentUri: string;
  documentHost: string;
  violatedDirective: CspDirective;
  effectiveDirective: CspDirective;
  blockedUri: string;
  blockedHost: string;
  disposition: "enforce" | "report";
  originalPolicy: string;
  statusCode?: number;
  sourceFile?: string;
  lineNumber?: number;
  columnNumber?: number;
  sample?: string;
  referrer?: string;
  userAgent?: string;
  reportFormat: "csp-report" | "reporting-api";
  dedupeHash: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  nextCursor?: string;
  total?: number;
}

export type DateRange = "24h" | "7d" | "30d" | "90d";
export type Granularity = "hour" | "day" | "week";

export interface AnalyticsSummary {
  totalReports: number;
  uniqueViolations: number;
  uniqueDocuments: number;
  topDirective: string | null;
  topBlockedHost: string | null;
  changeFromPrevious: number | null;
}

export interface GroupedCount {
  key: string;
  label: string;
  count: number;
  percentage: number;
}

export interface TimelineDataPoint {
  timestamp: string;
  count: number;
  uniqueViolations: number;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
}

export interface TenantSummary {
  id: string;
  name: string;
  plan: Plan;
}

export interface TenantProfile extends TenantSummary {
  /**
   * Whether the tenant has a real Descope organization rather than the
   * placeholder one provisioning creates. Only `/auth/me` computes it; `/me`
   * and `/tenants/setup` return a bare `TenantSummary`.
   */
  isOrgSetupComplete: boolean;
  /**
   * When the tenant's first site was created, or null if it has never had
   * one (task 495). Never cleared, so it is what tells an onboarded tenant
   * that has deleted every site (the dashboard, empty) from one that has not
   * finished onboarding. Only `/auth/me` returns it.
   */
  firstSiteCreatedAt: string | null;
}

export interface AuthMeResponse {
  user: UserProfile;
  tenant: TenantProfile;
  isNewUser: boolean;
}

// User Management Types
export type UserRole = "owner" | "admin" | "member" | "viewer";
export type UserStatus = "active" | "invited";

export interface TeamUser {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
  lastLoginAt: string | null;
  /**
   * How they last signed in, or null for somebody who never has (task 349).
   * A string rather than a union for the same reason as `removedReason`: the
   * spec cannot narrow a nullable enum. `LOGIN_METHOD_LABELS` names it.
   */
  lastLoginMethod: string | null;
}

export interface ListUsersResponse {
  users: TeamUser[];
  total: number;
  userCount: number;
  userLimit: number;
}

/**
 * The response headers that request a site's report types (task 311),
 * generated from its settings by `generateSetupHeaders` in core.
 */
export interface SetupInstructionsResponse {
  /** The site's ingestion URL, `https://{ingestCode}.ingest…/r`. */
  endpoint: string;
  /** The URL the site's chosen label names, or null (task 421). */
  customEndpoint: string | null;
  /**
   * `https://{hostname}/r` for the site's `active` report host on its own
   * domain, or null (task 422).
   */
  hostEndpoint: string | null;
  /**
   * The URL `headers` names: `hostEndpoint`, else `customEndpoint`, else
   * `endpoint`. The others keep working beside it.
   */
  preferredEndpoint: string;
  /**
   * The path-form URL a site created before task 417 was given, which keeps
   * working, or null.
   */
  legacyEndpoint: string | null;
  endpointName: "default" | "headerhawk";
  /** The whole block, `Reporting-Endpoints` first, each line once. */
  headers: string[];
  /** Every available type, in registry order, with the lines it asks for. */
  perType: SetupTypeHeaders[];
  /**
   * The discovery policy's line while the site runs policy discovery (task
   * 407), also last in `headers`; null otherwise.
   */
  discoveryHeader: string | null;
}

/** `GET` and `PUT /sites/{siteId}/policy-discovery` (task 407). */
export interface PolicyDiscoveryResponse {
  status: "off" | "running" | "stopped";
  startedAt: string | null;
  endsAt: string | null;
  sampleRate: number;
  daysRun: number;
  reportsKept: number | null;
  reportsEstimated: number | null;
  sourcesSeen: number | null;
  lastReportAt: string | null;
  /** Where the discovery policy reports: the site's URL with `/discovery`. */
  endpoint: string;
  /** The `Reporting-Endpoints` line naming both endpoints. */
  reportingEndpoints: string;
  /** The discovery policy's `Content-Security-Policy-Report-Only` line. */
  header: string;
}

/**
 * `GET /sites/{siteId}/policy-discovery/proposal` (task 407), as the spec
 * states it: `decision` and `unchecked` are plain strings there, so
 * `toProposal` in `@/lib/policy-draft` narrows them to core's types.
 */
export interface PolicyProposalResponse {
  status: "off" | "running" | "stopped";
  startedAt: string | null;
  truncated: boolean;
  /** Without a payment page, nothing can be flagged `payment_only`. */
  paymentPagesMarked: boolean;
  directives: Array<{
    name: string;
    pinned: boolean;
    values: string[];
    sources: Array<{
      source: string;
      kind: "self" | "origin" | "scheme" | "wildcard";
      evidence: {
        reports: number;
        estimated: number;
        browsers: string[];
        pages: number;
        examplePages: string[];
        firstSeen: string;
        lastSeen: string;
        reportedAs: string[];
      } | null;
      proposed: boolean;
      unchecked: string | null;
      decision: string | null;
      included: boolean;
      /**
       * Who the catalog says it belongs to, or a model suggests for a host
       * the catalog does not know (`origin: "model"`, task 465).
       */
      identity: SourceIdentity | null;
      /** A model is being asked about it; read again shortly. */
      identityPending: boolean;
      firstParty: boolean;
      sourceFlags: SourceFlag[];
    }>;
  }>;
  flags: Array<{
    kind:
      | "inline_script"
      | "inline_style"
      | "eval"
      | "wasm_eval"
      | "framed"
      | "base_uri"
      | "plugin"
      | "scheme_not_proposed"
      | "script_src_override";
    directive: string;
    message: string;
    reports: number;
    samples: string[];
    pages: string[];
  }>;
  policy: string;
  header: string;
  reportingEndpoints: string;
  grade: {
    grade: "A+" | "A" | "B" | "C" | "D" | "F";
    score: number;
    findings: Array<{
      id: string;
      severity: "high" | "medium" | "low" | "info";
      title: string;
      remediation: string;
    }>;
  };
}

/** `PUT /sites/{siteId}/ingest-label` (task 421). */
export interface IngestLabelResponse {
  label: string;
  /** The URL the label names, `https://{label}.ingest…/r`. */
  endpoint: string;
}

/** Where a report host on the customer's own domain is in its life (task 422). */
export type IngestHostStatus =
  | "awaiting_dns"
  | "issuing"
  | "active"
  | "unrouted"
  | "failed"
  | "grace"
  | "suspended"
  | "removing";

/** A report host on the customer's own domain (task 422). */
export interface IngestHost {
  id: string;
  hostname: string;
  /** The URL the site's headers name, `https://{hostname}/r`. */
  endpoint: string;
  status: IngestHostStatus;
  statusReason: string | null;
  statusChangedAt: string;
  graceEndsAt: string | null;
  createdAt: string;
}

export interface IngestHostListResponse {
  hosts: IngestHost[];
  /** `hosts.ingest…`, or null where the environment has no report host edge. */
  cnameTarget: string | null;
}

export interface SetupTypeHeaders {
  type: ReportType;
  enabled: boolean;
  lines: string[];
}

export interface ListSitesResponse {
  sites: Site[];
  siteCount: number;
  siteLimit: number;
  /**
   * When the tenant's first site was created, or null if it has never had
   * one (task 495). With `sites` empty, set means the tenant onboarded and
   * deleted its sites, so `RequireAuth` keeps it on the dashboard.
   */
  firstSiteCreatedAt: string | null;
}

export interface InviteUserRequest {
  email: string;
  name?: string;
  role: "admin" | "member" | "viewer";
}

export interface ProfileResponse {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface GetProfileResponse {
  user: ProfileResponse;
  tenant: TenantSummary | null;
}

export interface AuthMeRequest {
  email?: string;
  name?: string;
  /**
   * Where the signup came from (task 299). Read by the API only on the call
   * that creates a tenant, and recorded on that tenant's `signup` lifecycle
   * event; ignored for a caller who already has one. `spec-conformance.ts`
   * asserts this against the spec's own enum.
   */
  signupSource?: "scan";
}

export interface TenantSetupRequest {
  name: string;
}

export interface TenantSetupResponse {
  /**
   * The tenant as the setup route returns it. It carries no
   * `isOrgSetupComplete` - completion is implied by the call having succeeded
   * - so callers updating a cached profile must set that flag themselves.
   */
  tenant: TenantSummary;
  profile: UserProfile | null;
}

export type { Plan } from "@headerhawk/core";

// Usage Types
export type UsageStatus =
  "under" | "warning" | "soft_exceeded" | "hard_exceeded";

export interface UsageResponse {
  reportCount: number;
  /** How many of `reportCount` were script hash reports. */
  scriptHashReportCount: number;
  softExceededCount: number;
  limit: number;
  hardLimit: number;
  status: UsageStatus;
  percentUsed: number;
  periodStart: string;
  periodEnd: string;
  plan: Plan;
}

export interface TenantUpdateResponse {
  /**
   * The tenant as `PATCH /tenant` returns it. Like the setup route's, it
   * carries no `isOrgSetupComplete` - a rename cannot change whether the
   * organization is provisioned - so callers updating a cached profile must
   * preserve that flag themselves.
   */
  tenant: TenantSummary;
}

export type BillingInterval = "monthly" | "annual";

export interface CreateCheckoutSessionRequest {
  plan: Plan;
  interval?: BillingInterval;
  successUrl: string;
  cancelUrl: string;
  /**
   * Whether to attach a free trial when the plan offers one. Defaults to true.
   *
   * Set to false to proceed with paid checkout after the API has answered
   * `trial_not_eligible` (409). It can only remove a trial, never grant one.
   */
  allowTrial?: boolean;
}

export interface CreateCheckoutSessionResponse {
  url: string;
}

export interface CreatePaymentMethodSessionRequest {
  successUrl: string;
  cancelUrl: string;
}

export type SubscriptionStatus =
  "active" | "past_due" | "canceled" | "incomplete" | "trialing";

/**
 * A change Stripe will make at the end of the current period: a downgrade
 * (task 324), a move from annual to monthly billing (task 412), or both.
 * Until `effectiveDate` the tenant keeps its current plan and every limit it
 * sets.
 */
export interface PendingPlanChange {
  /** The current plan when only the interval changes. */
  plan: Exclude<Plan, "free">;
  /**
   * `monthly` or `annual`. A plain string, as the spec declares it: a
   * nullable enum cannot pass API Gateway's import. Narrow it with
   * `isBillingInterval`.
   */
  interval: string | null;
  effectiveDate: string;
}

export interface SubscriptionInfo {
  status: SubscriptionStatus;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  /**
   * How often the current price bills (task 412): `monthly` or `annual`, as a
   * plain string like `pendingPlanChange.interval`. Null for a contract's
   * price, and for a subscription recorded before the interval was.
   */
  interval: string | null;
  pendingPlanChange: PendingPlanChange | null;
  /**
   * HeaderHawk gave this plan away (task 472): the published price with a
   * 100%-off coupon, renewing at no charge. The plan cannot be changed here
   * (`POST /subscription/update` answers 409 `complimentary_plan`); it can
   * still be cancelled in the Billing Portal.
   */
  complimentary: boolean;
}

export interface SubscriptionStatusResponse {
  plan: Plan;
  subscription: SubscriptionInfo | null;
}

/**
 * The subscription a just-completed checkout produced. No pending change: a
 * checkout starts a subscription or adds a card, and neither schedules one.
 * No interval either: the billing page reads it from the refetch that follows.
 * Never complimentary, which only an operator grants.
 */
export type CheckoutSubscriptionInfo = Omit<
  SubscriptionInfo,
  "pendingPlanChange" | "interval" | "complimentary"
>;

export interface CheckoutVerifyResponse {
  plan: Plan;
  subscription: CheckoutSubscriptionInfo | null;
}

export interface CreatePortalSessionRequest {
  returnUrl: string;
}

export interface PortalSessionResponse {
  url: string;
}

export interface UpdateSubscriptionRequest {
  plan: Exclude<Plan, "free">;
  /** Omitted: the interval the subscription is billed at now (task 412). */
  interval?: BillingInterval;
}

export interface UpdateSubscriptionResponse {
  success: boolean;
  effectiveDate: string;
  /** The change waits for the period end: a lower plan, or annual to monthly. */
  isDowngrade: boolean;
}

// Reports Explorer Types (Tinybird-powered)
export type DeviceType = "desktop" | "mobile" | "tablet" | "unknown";

/**
 * The report types the Explorer lists (tasks 312 and 313): CSP violations,
 * the security policies and the browser changes. The registry's listed
 * types, as the API names them.
 */
export type ListedReportType =
  | IssueReportType
  | "deprecation"
  | "intervention"
  // Listed in the Explorer only (task 314); Issues refuses them.
  | "network-error"
  | "crash";

/**
 * The report types Issues triages: every listed type but the browser
 * changes, which have a page of their own (task 313).
 */
export type IssueReportType =
  | "csp-violation"
  | "integrity-violation"
  | "coep"
  | "coop"
  | "permissions-policy-violation"
  | "document-policy-violation"
  | "connection-allowlist";

export type SecurityReportType = Exclude<ListedReportType, "csp-violation">;

export interface ExplorerReportSummary {
  reportType: "csp-violation";
  id: string;
  siteId: string;
  receivedAt: string;
  documentHost: string;
  effectiveDirective: string;
  blockedHost: string;
  disposition: "enforce" | "report";
  browserName: string | null;
}

export interface ExplorerReport extends ExplorerReportSummary {
  documentUri: string;
  violatedDirective: string;
  blockedUri: string;
  statusCode: number | null;
  sourceFile: string | null;
  lineNumber: number | null;
  columnNumber: number | null;
  sample: string | null;
  originalPolicy: string;
  referrer: string | null;
  userAgent: string | null;
  browserName: string | null;
  browserVersion: string | null;
  osName: string | null;
  osVersion: string | null;
  deviceType: DeviceType;
  reportFormat: string;
}

/**
 * A security policy report in the Explorer (task 312). Every type has every
 * field; one the type does not carry is null, and the registry's
 * `listing.columns` says which to show.
 */
export interface SecurityReportSummary {
  id: string;
  reportType: SecurityReportType;
  /** The type string the browser sent, e.g. `potential-permissions-policy-violation`. */
  storedType: SecurityReportType | "potential-permissions-policy-violation";
  siteId: string;
  receivedAt: string;
  documentUrl: string;
  documentHost: string;
  disposition: string | null;
  browserName: string | null;
  feature: string | null;
  blockedUrl: string | null;
  blockedOrigin: string | null;
  destination: string | null;
  effectivePolicy: string | null;
  otherUrl: string | null;
  otherOrigin: string | null;
  sourceFile: string | null;
  property: string | null;
  lineNumber: number | null;
  columnNumber: number | null;
  message: string | null;
}

export interface SecurityReport extends SecurityReportSummary {
  sampleRate: number;
  userAgent: string | null;
  browserVersion: string | null;
  osName: string | null;
  osVersion: string | null;
  deviceType: DeviceType;
  /** The body the browser sent, capped at 16 KB. */
  body: Record<string, unknown>;
}

/** A row of the Explorer's list, whichever type it reads. */
export type ExplorerRow = ExplorerReportSummary | SecurityReportSummary;

/** One entry of the Explorer's type selector (`GET /reports/types`). */
export interface ReportTypeCount {
  reportType: ListedReportType;
  label: string;
  /** Reports that arrived: stored, scaled by each one's sample rate. */
  count: number;
  sampled: boolean;
}

/**
 * The column filters a security policy type may take (task 312), by the name
 * the API gives them.
 */
export interface SecurityColumnFilters {
  feature?: string;
  blockedUrl?: string;
  blockedOrigin?: string;
  destination?: string;
  effectivePolicy?: string;
  otherOrigin?: string;
  sourceFile?: string;
}

export interface ExplorerFilters extends SecurityColumnFilters {
  /** The type the view reads; CSP violations when omitted (task 312). */
  reportType?: ListedReportType;
  siteIds?: string[];
  directive?: string;
  blockedHost?: string;
  disposition?: "enforce" | "report";
  documentHost?: string;
  browserName?: string;
  osName?: string;
  deviceType?: DeviceType;
  startTime?: string;
  endTime?: string;
  /** `payment`: only the sites' payment pages (task 306). */
  pageScope?: PageScope;
}

// Grouped Reports Types
export type GroupDimension =
  | "directive"
  | "blocked_host"
  | "document_host"
  | "disposition"
  | "browser_name"
  | "os_name"
  | "device_type"
  | "site_id"
  | "feature"
  | "blocked_url"
  | "blocked_origin"
  | "destination"
  | "effective_policy"
  | "other_origin"
  | "source_file";

export interface GroupedReport {
  groupValue1: string;
  groupValue2: string | null;
  count: number;
  lastSeen: string;
  firstSeen: string;
  trendPercent: number | null;
}

export interface GroupedReportsResponse {
  groups: GroupedReport[];
  groupBy: GroupDimension[];
}

export interface GroupedReportsParams extends ExplorerFilters {
  groupBy: [GroupDimension] | [GroupDimension, GroupDimension];
  limit?: number;
}

export interface ExplorerListParams extends ExplorerFilters {
  limit?: number;
  offset?: number;
}

export interface ExplorerListResponse {
  items: ExplorerRow[];
  total: number;
  hasMore: boolean;
}

export interface HistogramBucket {
  time: string;
  count: number;
}

export type BucketSize = "1m" | "5m" | "15m" | "1h" | "1d";

export interface HistogramResponse {
  buckets: HistogramBucket[];
  bucketSize: BucketSize;
}

export interface FacetValue {
  value: string;
  count: number;
}

export interface FacetsResponse {
  facets: {
    directive?: FacetValue[];
    blocked_host?: FacetValue[];
    disposition?: FacetValue[];
    document_host?: FacetValue[];
    browser_name?: FacetValue[];
    os_name?: FacetValue[];
    device_type?: FacetValue[];
    site_id?: FacetValue[];
    feature?: FacetValue[];
    blocked_url?: FacetValue[];
    blocked_origin?: FacetValue[];
    destination?: FacetValue[];
    effective_policy?: FacetValue[];
    other_origin?: FacetValue[];
    source_file?: FacetValue[];
  };
}

export interface CountResponse {
  /** Reports stored, which the list pages through. */
  total: number;
  /** Reports that arrived, before sampling. */
  estimate: number;
  sampled: boolean;
}

// Issues Types (aggregated by signature)
export type ConfidenceLevel = "high" | "medium" | "low" | "suppressed";

export interface Issue {
  signature: string;
  /** The issue's type (task 312); with the signature, its identity. */
  reportType: IssueReportType;
  siteId: string;
  /** A CSP directive, or the first of the type's grouping columns. */
  subject: string;
  /** The host of the blocked URL or of the script that caused it. */
  targetHost: string;
  /** CSP only. */
  directive: string | null;
  /** CSP only. */
  blockedHost: string | null;
  description: string;
  reportCount: number;
  /** Reports that arrived before sampling; `reportCount` when unsampled. */
  reportCountEstimate: number;
  /** Whether `reportCountEstimate` is an estimate. */
  sampled: boolean;
  signalCount: number;
  noiseCount: number;
  browserFamilyCount: number;
  affectedPages: number;
  firstSeen: string;
  lastSeen: string;
  confidence: ConfidenceLevel;
  confidenceReason: string;
  /** Where it stands (task 448): open unless marked expected or fixed. */
  state: IssueState;
  /** The latest decision; null when nobody has decided on it. */
  decision: IssueDecisionRecord | null;
  /** Marked fixed, and reported again since: open. */
  reopened: boolean;
}

/** Where an issue stands (task 448). */
export type IssueState = "open" | "expected" | "fixed";

/** One decision on an issue (task 448). Never changed or removed. */
export interface IssueDecisionRecord {
  id: string;
  state: IssueState;
  note: string | null;
  decidedBy: ScriptDecider;
  decidedAt: string;
}

export interface IssueDecisionRequest {
  /** `open` reopens an expected or fixed issue. */
  state: IssueState;
  note?: string | null;
}

export interface IssueDecisionResponse {
  decision: IssueDecisionRecord;
  state: IssueState;
}

export interface IssueDecisionsResponse {
  /** Newest first. */
  decisions: IssueDecisionRecord[];
}

export interface IssuesListParams {
  /** The types to list; every listed type when omitted (task 312). */
  reportTypes?: IssueReportType[];
  directive?: string;
  startTime?: string;
  endTime?: string;
  includeNoise?: boolean;
  /** `payment`: only the sites' payment pages (task 306). */
  pageScope?: PageScope;
  /** By decision (task 448); the API lists open issues when omitted. */
  state?: IssueState | "all";
  /**
   * Only the issues first reported since then (task 449). The API reads
   * their first report from the retention window's start, in place of
   * `startTime`.
   */
  firstSeenSince?: string;
  limit?: number;
  offset?: number;
}

export interface IssuesListResponse {
  issues: Issue[];
  total: number;
}

export interface IssueReport {
  reportType: "csp-violation";
  id: string;
  siteId: string;
  receivedAt: string;
  documentUri: string;
  documentHost: string;
  effectiveDirective: string;
  blockedUri: string;
  blockedHost: string;
  disposition: "enforce" | "report";
  sourceFile: string | null;
  lineNumber: number | null;
  sample: string | null;
}

export interface IssueReportsListParams {
  limit?: number;
  offset?: number;
}

export interface IssueReportsListResponse {
  items: Array<IssueReport | SecurityReport>;
  total: number;
  hasMore: boolean;
}

export interface NoiseStatsResponse {
  totalReports: number;
  signalReports: number;
  likelyNoiseReports: number;
  knownNoiseReports: number;
  noisePercentage: number;
}

export interface NoiseStatsParams {
  startTime?: string;
  endTime?: string;
}

// Alerting types (tasks 304 and 305): destinations configured once per tenant,
// tenant-level rules that name them, the delivery history and per-site
// coverage. Shapes follow docs/api-specs/dashboard-api.yaml.
export type AlertRuleDelivery = "immediate" | "daily" | "weekly";

export type AlertDestinationType = "email" | "webhook" | "slack";

/**
 * Whether an address may receive alerts. A tenant user's own address is
 * `confirmed`; any other receives nothing until its confirmation link is
 * followed (`pending` once the link has been sent).
 */
export type AlertEmailConfirmation = "confirmed" | "pending" | "unconfirmed";

export interface AlertEmailRecipient {
  kind: "address" | "role";
  address?: string;
  minRole?: UserRole;
  confirmation?: AlertEmailConfirmation;
}

export interface AlertDestination {
  id: string;
  name: string;
  type: AlertDestinationType;
  email: { recipients: AlertEmailRecipient[] } | null;
  /** The secret itself is never returned; `hasSecret` says whether one is stored. */
  webhook: { url: string; hasSecret: boolean } | null;
  slack: { channelId: string; channelName: string } | null;
  createdAt: string;
  updatedAt: string;
  /** Null unless deliveries here are failing (task 336). */
  failingSince: string | null;
  /** The latest failure's reason; still set after a recovery. */
  lastFailureDetail: string | null;
  lastDeliveredAt: string | null;
}

export interface ListAlertDestinationsResponse {
  destinations: AlertDestination[];
  limit: number;
}

export type AlertEmailRecipientInput =
  { kind: "address"; address: string } | { kind: "role"; minRole: UserRole };

export interface CreateAlertDestinationRequest {
  name: string;
  type: AlertDestinationType;
  email?: { recipients: AlertEmailRecipientInput[] };
  webhook?: { url: string; secret?: string };
  slack?: { channelId: string };
}

export interface UpdateAlertDestinationRequest {
  name?: string;
  email?: { recipients: AlertEmailRecipientInput[] };
  /** An omitted `secret` keeps the stored one; `null` removes it. */
  webhook?: { url?: string; secret?: string | null };
  slack?: { channelId: string };
}

export interface AlertRuleRef {
  id: string;
  name: string;
}

export interface DeleteAlertDestinationResponse {
  deleted: boolean;
  /** Rules disabled because this was their last destination. */
  disabledRules: AlertRuleRef[];
}

/**
 * Why something other than the tenant disabled a rule. The spec types it as a
 * string rather than an enum, so an unknown value is still a string here.
 */
export type AlertRuleDisabledReason =
  "destinations_deleted" | "sites_deleted" | "plan";

export interface AlertRule {
  id: string;
  name: string;
  enabled: boolean;
  disabledReason: string | null;
  /**
   * `all`: every type the plan routes with this delivery, including types
   * added later (task 315); `eventTypes` is then that list as resolved now.
   */
  eventTypeScope: "all" | "selected";
  eventTypes: string[];
  siteScope: "all" | "selected";
  /** Empty unless `siteScope` is `selected`. */
  siteIds: string[];
  delivery: AlertRuleDelivery;
  /** `payment`: only candidates on the site's payment pages (task 306). */
  pageScope: PageScope;
  cooldownHours: number;
  destinationIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ListAlertRulesResponse {
  rules: AlertRule[];
  limit: number;
}

export interface CreateAlertRuleRequest {
  name: string;
  enabled?: boolean;
  /** Defaults to `selected`. With `all`, send no `eventTypes`. */
  eventTypeScope?: "all" | "selected";
  eventTypes?: string[];
  siteScope: "all" | "selected";
  siteIds?: string[];
  delivery: AlertRuleDelivery;
  /** Defaults to `any`. `payment` needs Professional or Business. */
  pageScope?: PageScope;
  /** Immediate delivery only; the API refuses it on a digest rule. */
  cooldownHours?: number;
  destinationIds: string[];
}

export type UpdateAlertRuleRequest = Partial<CreateAlertRuleRequest>;

export interface AlertEventTypeInfo {
  type: string;
  label: string;
  description: string;
  /** Whether a rule may deliver it immediately. */
  immediate: boolean;
  /** Whether a rule may deliver it in a digest. */
  digest: boolean;
}

export interface ListAlertEventTypesResponse {
  eventTypes: AlertEventTypeInfo[];
}

export interface AlertDeliveryRecord {
  id: string;
  alertId: string;
  kind: "immediate" | "digest" | "test";
  outcome: "delivered" | "rejected" | "dropped" | "skipped";
  /** Why it was rejected or dropped. */
  detail: string | null;
  destinationId: string;
  /**
   * The destination's name and type when it was sent. Both are null when the
   * destination was deleted between routing and delivery, which is recorded
   * as `dropped`.
   */
  destinationName: string | null;
  /**
   * `email`, `webhook` or `slack`. A string rather than the union, matching
   * the spec, which cannot declare a nullable enum: a `null` member is what
   * `nullable` needs, and CloudFormation refuses a null in a template.
   */
  destinationType: string | null;
  eventType: string | null;
  siteId: string | null;
  /** The sites a digest covered. */
  siteIds: string[];
  ruleIds: string[];
  dedupKey: string | null;
  /**
   * What the alert said, in one line (task 358): which script left which
   * page, or both sides of a move. Null for a test, a digest, and alerts
   * recorded before the field.
   */
  summary: string | null;
  createdAt: string;
}

export interface ListAlertDeliveriesParams {
  limit?: number;
  cursor?: string;
  destinationId?: string;
  siteId?: string;
  eventType?: string;
  /** Only rows written at or after this (task 449). */
  startTime?: string;
}

export interface ListAlertDeliveriesResponse {
  deliveries: AlertDeliveryRecord[];
  nextCursor: string | null;
}

export interface TestAlertDestinationResponse {
  /** The id the delivery row will carry. */
  alertId: string;
}

export interface SiteAlertCoverageResponse {
  siteId: string;
  eventTypes: Array<{
    type: string;
    rules: Array<{ id: string; name: string; delivery: AlertRuleDelivery }>;
  }>;
}

export interface ConfirmAlertEmailResponse {
  confirmed: boolean;
}

// Slack Integration Types
export interface SlackIntegrationStatus {
  connected: boolean;
  teamName?: string;
  connectedAt?: string;
}

export interface SlackAuthorizeUrlResponse {
  authorizeUrl: string;
}

export interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
}

export interface SlackChannelsResponse {
  channels: SlackChannel[];
}

// Monitored pages (task 306)

/** Which pages a view or an alert rule covers. */
export type PageScope = "any" | "payment";

export type MonitoredPageKind = "payment" | "other";

// API keys (task 437, ADR 014)

/** A key's state, derived by the API from its dates (`apiKeyState`). */
import type { ApiKeyState } from "@headerhawk/core/api-keys/rules";
export type { ApiKeyState };

/** An API key as every list shows it. Never the secret. */
export interface ApiKey {
  id: string;
  name: string;
  /** The first 8 characters after the scheme, to match a key to its row. */
  prefix: string;
  /** Fixed: API access is read-only. */
  role: "viewer";
  state: ApiKeyState;
  /** Email and name are null once the creator has left the organisation. */
  createdBy: { id: string; email: string | null; name: string | null };
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  suspendedAt: string | null;
}

/** The one response that carries the key itself, shown once. */
export interface CreatedApiKey extends ApiKey {
  key: string;
}

export interface ListApiKeysResponse {
  keys: ApiKey[];
  /** The most live (not revoked, not expired) keys a tenant may hold. */
  limit: number;
}

export interface CreateApiKeyRequest {
  name: string;
  expiresInDays: number;
}

// Connected assistants: the member's own MCP grants (task 440)

export interface ConnectedAssistant {
  /** The grant's id, which disconnecting takes. */
  id: string;
  /** The name the assistant registered with: self-declared. */
  name: string;
  clientId: string;
  scopes: string[];
  connectedAt: string | null;
}

export interface ListConnectedAssistantsResponse {
  assistants: ConnectedAssistant[];
  /** The address to give an assistant, or null where there is no MCP server. */
  mcpUrl: string | null;
}

// Verified email domains (task 347)

export interface TenantDomain {
  id: string;
  domain: string;
  verified: boolean;
  /** When the domain was first proved; later checks do not re-stamp it. */
  verifiedAt: string | null;
  /**
   * How it was proved: `dns`, `operator` when HeaderHawk verified it for the
   * organisation (task 352), or `none` while unverified.
   */
  verificationMethod: "none" | "dns" | "operator";
  lastCheckedAt: string | null;
  /**
   * What the last check found, or null before any check has run: `verified`,
   * `no_record`, `not_found`, `lookup_failed` or `claimed_elsewhere`.
   *
   * A string rather than a union, matching the spec, which cannot narrow it:
   * a nullable enum has to list `null` to accept one, and API Gateway's import
   * refuses that. Nothing in the dashboard switches on it — `verified` and
   * `lastCheckMessage` are what it renders — so the union would buy nothing
   * that the spec could not check.
   */
  lastCheckOutcome: string | null;
  /** The message that goes with the outcome, written by the API. */
  lastCheckMessage: string | null;
  /** The exact record to publish; never composed in the bundle. */
  record: { type: "TXT"; name: string; value: string };
  createdAt: string;
}

export interface ListTenantDomainsResponse {
  domains: TenantDomain[];
  limit: number;
}

export interface VerifyTenantDomainResponse {
  domain: TenantDomain;
  /** TXT records found at the challenge name, ours or not. */
  recordsFound: number;
}

// Single sign-on (task 348)

/** What a tenant's SAML connection was configured from. */
export interface TenantSsoSettings {
  source: "metadata" | "manual";
  metadataUrl: string | null;
  entityId: string | null;
  signInUrl: string | null;
  /** The IdP's X.509 signing certificate, base64. Public by construction. */
  certificate: string | null;
  domains: string[];
  updatedAt: string;
}

/** The two values to register with the identity provider. */
export interface TenantSsoServiceProvider {
  acsUrl: string;
  entityId: string;
}

export interface TenantSsoResponse {
  /** Whether the identity platform currently holds a connection. */
  configured: boolean;
  /** Null until the tenant has configured one; survives a plan change. */
  settings: TenantSsoSettings | null;
  /**
   * Why settings are held with no live connection: `plan_change`, or null.
   *
   * A string rather than a union for the same reason as
   * `TenantDomain.lastCheckOutcome`: the spec cannot narrow a nullable enum,
   * and the screen renders one sentence rather than switching on it.
   */
  removedReason: string | null;
  /**
   * Null until single sign-on has been set up (task 510): the two values are
   * derived from the identity platform's organization, and reading this never
   * creates one.
   */
  serviceProvider: TenantSsoServiceProvider | null;
  domainLimit: number;
  enforcement: TenantSsoEnforcement;
}

/**
 * Whether the identity provider is the only way in for the covered domains
 * (task 349), and whether that can be switched on yet.
 */
export interface TenantSsoEnforcement {
  enabled: boolean;
  canEnable: boolean;
  /**
   * The first unmet condition, or null. A string rather than a union for the
   * same reason as `removedReason`: the spec cannot narrow a nullable enum.
   * The card renders the API's message, not a switch on this.
   */
  blocker: string | null;
  /** The domains enforcement covers. Everyone else keeps every way in. */
  domains: string[];
}

export interface TenantSsoEnforcementResponse {
  enforcement: TenantSsoEnforcement;
}

/** The body `PUT /tenant/sso` takes, in one of its two forms. */
export type ConfigureTenantSsoRequest =
  | { source: "metadata"; metadataUrl: string; domains: string[] }
  | {
      source: "manual";
      entityId: string;
      signInUrl: string;
      certificate: string;
      domains: string[];
    };

export interface MonitoredPage {
  id: string;
  siteId: string;
  label: string;
  kind: MonitoredPageKind;
  /** `scheme://host/path`, normalised; `*` and `**` in the path. */
  matchPattern: string;
  /** Required for a payment page. */
  scanUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListMonitoredPagesResponse {
  pages: MonitoredPage[];
  limit: number;
  /** Each page's latest scheduled scan (task 192); a page never scanned has none. */
  latestScans: LatestPageScan[];
}

// Header monitoring (task 192)

/** The free scanner's letter grade. */
export type ScanGrade = "A+" | "A" | "B" | "C" | "D" | "F";

export type PageScanStatus = "complete" | "failed";

/**
 * Why a scan failed: the free scanner's unscannable reasons, and
 * `not_allowed` when the page or a redirect left the site's hosts.
 */
export type PageScanFailureReason =
  | "bot_challenge"
  | "access_denied"
  | "server_error"
  | "timeout"
  | "tls_failure"
  | "connection_failed"
  | "too_many_redirects"
  | "not_html"
  | "not_allowed";

export type WatchedHeaderName =
  | "content-security-policy"
  | "content-security-policy-report-only"
  | "strict-transport-security"
  | "x-frame-options"
  | "x-content-type-options"
  | "referrer-policy"
  | "permissions-policy";

/** Each watched header's comparable value; null when the page did not send it. */
export type WatchedHeaders = Record<WatchedHeaderName, string | null>;

export interface HeaderChange {
  header: WatchedHeaderName;
  change: "added" | "removed" | "changed";
  before: string | null;
  after: string | null;
}

export interface PageScanChanges {
  headers: HeaderChange[];
  /**
   * The last complete scan's grade (a `ScanGrade`), when this one is lower
   * under the same rubric. A plain string on the wire: the spec cannot
   * express a nullable enum (see `AlertDelivery.destinationType`).
   */
  gradeDroppedFrom: string | null;
  /** The rubric changed since the last complete scan, so grades were not compared. */
  rebaselined: boolean;
}

export interface PageScanSummary {
  id: string;
  pageId: string;
  scannedAt: string;
  /** The page's scan URL when it was scanned. */
  scanUrl: string;
  status: PageScanStatus;
  /** A `PageScanFailureReason`, as a plain string on the wire. */
  failureReason: string | null;
  failureMessage: string | null;
  /** Failures in a row, this one included; 0 for a complete scan. */
  consecutiveFailures: number;
  algorithmVersion: number;
  /** A `ScanGrade`, as a plain string on the wire. */
  grade: string | null;
  score: number | null;
  finalUrl: string | null;
  statusCode: number | null;
  headers: WatchedHeaders | null;
  changes: PageScanChanges;
}

/**
 * One scan with its graded result. The spec declares `result` as an open
 * object; it is the free scanner's `ScanResult` (`@headerhawk/core/scan`),
 * which `readScanResult` in `lib/scans` narrows it to.
 */
export interface PageScan extends PageScanSummary {
  result: { [key: string]: unknown } | null;
  /**
   * The page's scripts, each fetched and hashed (task 333), in document
   * order. Null unless the page is a payment page on a plan that authorizes
   * scripts, and on every scan from before that.
   */
  scripts: ScannedScript[] | null;
}

/** One script tag with a `src` that a payment page's scan found (task 333). */
export interface ScannedScript {
  src: string;
  url: string | null;
  identityUrl: string | null;
  sameOrigin: boolean;
  integrity: string | null;
  outcome: "hashed" | "failed" | "refused" | "skipped";
  /** `sha256-<base64>`, as a browser reports it; null unless hashed. */
  hash: string | null;
  size: number | null;
  problem: { reason: string; message: string } | null;
  cors: {
    verdict: "readable" | "credentialed" | "not_readable";
    allowOrigin: string | null;
    allowCredentials: boolean;
    redirected: boolean;
    failingHop: {
      url: string;
      statusCode: number;
      originSent: string;
      allowOrigin: string | null;
    } | null;
    message: string;
  } | null;
}

export interface ListPageScansResponse {
  scans: PageScanSummary[];
  /** The `before` for the next page, or null when there is none. */
  nextBefore: string | null;
  /**
   * The page's latest complete scan, however many failures followed it;
   * absent when it has never been scanned successfully.
   */
  latestComplete?: PageScanSummary;
}

export interface LatestPageScan {
  id: string;
  pageId: string;
  scannedAt: string;
  status: PageScanStatus;
  /** A `ScanGrade`, as a plain string on the wire. */
  grade: string | null;
  /** A `PageScanFailureReason`, as a plain string on the wire. */
  failureReason: string | null;
  consecutiveFailures: number;
  /** What it found different from the scan before (task 374). */
  changes: PageScanChanges;
}

/** A scan that recorded a change, as Headers › Changes lists it (task 374). */
export interface PageChange {
  /** The scan's id, which the page detail opens with `?scan=`. */
  id: string;
  scannedAt: string;
  /** The scan's own grade, a `ScanGrade` as a plain string on the wire. */
  grade: string | null;
  changes: PageScanChanges;
  page: {
    id: string;
    label: string;
    kind: MonitoredPageKind;
    /** The path of the URL the scan fetched. */
    path: string;
  };
}

export interface ListPageChangesResponse {
  scans: PageChange[];
  /** The `cursor` for the next page, or null when there is none. */
  nextCursor: string | null;
  /** Scans in the window that recorded a change, across every page. */
  count: number;
}

/** One policy a site's violation reports quoted, in comparable form. */
export interface CspPolicyVersion {
  /** The id a `csp.policy_changed` alert carries. */
  hash: string;
  disposition: "enforce" | "report";
  /** Directives and sources sorted, nonces as `'nonce-*'`; cut to 16 KB. */
  policy: string;
  truncated: boolean;
  firstSeen: string;
  lastSeen: string;
  reportCount: number;
}

export interface ListCspPoliciesResponse {
  policies: CspPolicyVersion[];
}

export interface CreateMonitoredPageRequest {
  label: string;
  kind: MonitoredPageKind;
  matchPattern: string;
  scanUrl?: string;
}

/** An empty `scanUrl` clears it, for a page that is not a payment page. */
export type UpdateMonitoredPageRequest = Partial<CreateMonitoredPageRequest>;

export interface PageSuggestion {
  url: string;
  suggestedPattern: string;
  violationCount: number;
  scriptReportCount: number;
  lastSeen: string;
  /** The pages already matching it; empty when unmarked. */
  pageIds: string[];
}

export interface PageSuggestionsResponse {
  suggestions: PageSuggestion[];
}

// Browser changes: deprecations and interventions (task 313)

export type BrowserChangeType = "deprecation" | "intervention";

export interface BrowserChangesParams {
  /** One site, or every site when omitted. */
  siteId?: string;
  startTime: string;
  endTime: string;
  reportType?: BrowserChangeType;
}

export interface BrowserChangesTiles {
  estimatedReports: number;
  reports: number;
  apis: number;
  withRemovalDate: number;
  /** The earliest removal from today on, or null when none is ahead. */
  soonestRemoval: string | null;
  sampled: boolean;
}

export interface BrowserChangesBrowser {
  /** The browser's name, or "" when its user agent was not recognised. */
  browser: string;
  estimatedReports: number;
}

export interface BrowserChangeItem {
  reportType: BrowserChangeType;
  featureId: string;
  sourceFile: string | null;
  lineNumber: number | null;
  message: string;
  anticipatedRemoval: string | null;
  estimatedReports: number;
  reports: number;
  sampled: boolean;
  pages: number;
  topPages: string[];
  browsers: string[];
  firstSeen: string;
  lastSeen: string;
}

export interface BrowserChangesResponse {
  tiles: BrowserChangesTiles;
  browsers: BrowserChangesBrowser[];
  items: BrowserChangeItem[];
  truncated: boolean;
}

export interface BrowserChangesSeries {
  /** null for the series holding every API outside the top five. */
  featureId: string | null;
  estimatedReports: number;
}

export interface BrowserChangesBucket {
  time: string;
  /** One per series, in `series` order. */
  counts: number[];
}

export interface BrowserChangesHistogram {
  bucketSize: "1m" | "5m" | "15m" | "1h" | "1d";
  series: BrowserChangesSeries[];
  buckets: BrowserChangesBucket[];
}

function browserChangesQuery(params: BrowserChangesParams): URLSearchParams {
  const searchParams = new URLSearchParams({
    startTime: params.startTime,
    endTime: params.endTime,
  });
  if (params.siteId) searchParams.set("siteId", params.siteId);
  if (params.reportType) searchParams.set("reportType", params.reportType);
  return searchParams;
}

// Script Hash Analytics Types

export interface ScriptStatsParams {
  siteId: string;
  startDate: string;
  endDate: string;
  /** `payment`: only the site's payment pages (task 306). */
  pageScope?: PageScope;
}

export interface ScriptStats {
  totalReports: number;
  uniqueOrigins: number;
  uniqueScripts: number;
  uniqueHashes: number;
}

export interface ScriptOrigin {
  origin: string;
  scriptCount: number;
  reportCount: number;
  lastSeen: string;
}

export interface ScriptOriginsParams {
  siteId: string;
  startDate: string;
  endDate: string;
  limit?: number;
  offset?: number;
  /** `payment`: only the site's payment pages (task 306). */
  pageScope?: PageScope;
}

export interface ScriptOriginsResponse {
  origins: ScriptOrigin[];
}

/**
 * A host a site's pages tried to send data to (task 366): the inventory's
 * first-seen dates, which outlive report retention, and the window's reports.
 * `firstSeenVia` null is a host the reports name that the inventory has no
 * record of.
 */
export interface Destination {
  siteId: string;
  host: string;
  directive: string | null;
  /** `discovery` or `violation`, null for a host the inventory never recorded. */
  firstSeenVia: string | null;
  firstSeenAt: string | null;
  firstRefusedAt: string | null;
  reportCount: number;
  pageCount: number;
  lastSeenAt: string | null;
  seenOnPaymentPage: boolean;
}

export interface DestinationsParams {
  /** One site, or every site when absent. */
  siteId?: string;
  startDate: string;
  endDate: string;
  /** `payment`: only destinations seen on a payment page (task 306). */
  pageScope?: PageScope;
}

export interface DestinationsResponse {
  destinations: Destination[];
  truncated: boolean;
}

/**
 * An origin a site's pages tried to load in a frame (task 367): the same
 * inventory and fields as a destination, from `frame-src` and `child-src`.
 */
export type FramedOrigin = Destination;

export interface FramedOriginsResponse {
  frames: FramedOrigin[];
  truncated: boolean;
}

/** Which inventory a Policy tab reads: destinations, or framed origins. */
export type InventoryKind = "egress" | "frame";

export interface ScriptInventoryItem {
  url: string;
  hash: string;
  origin: string;
  pageCount: number;
  reportCount: number;
  lastSeen: string;
}

export interface ScriptInventoryParams {
  siteId: string;
  startDate: string;
  endDate: string;
  origin?: string;
  limit?: number;
  offset?: number;
  /** `payment`: only the site's payment pages (task 306). */
  pageScope?: PageScope;
}

export interface ScriptInventoryResponse {
  scripts: ScriptInventoryItem[];
}

/** A script's current decision (task 307). */
export type ScriptStatus = "unreviewed" | "authorized" | "rejected";

/** What an authorization covers. */
export type ScriptIntegrityMode = "any_content" | "pinned_hashes";

export interface ScriptDecider {
  name: string | null;
  email: string;
}

/** What published package one hash's bytes came from (task 356). */
export interface ScriptHashIdentification {
  library: string;
  version: string;
  /** The file's path within the package, e.g. `dist/jquery.min.js`. */
  file: string;
  /** `jsdelivr` today. */
  source: string;
}

/** What has seen a hash (task 333). */
export type ScriptHashSource = "browser" | "scan";

export interface AuthorizedScriptHash {
  /** Empty for a browser sighting of a script loaded cross-origin without CORS. */
  hash: string;
  firstSeenAt: string;
  /** What saw it first. */
  source: ScriptHashSource;
  /** Every source that has seen it, `source` first. */
  sources: ScriptHashSource[];
  pinned: boolean;
  /** Null for a hash the fingerprint index does not hold. */
  identification: ScriptHashIdentification | null;
  /** HeaderHawk's verified copy of these bytes, or null (task 359). */
  capture: ScriptCapture | null;
}

/** What became of a capture attempt (task 359). */
export type ScriptCaptureState =
  "pending" | "captured" | "mismatched" | "refused" | "interrupted";

/** Why a capture was refused. */
export type ScriptCaptureRefusal =
  | "daily_cap"
  | "unknown_host"
  | "not_allowed"
  | "unsupported_hash"
  | "too_large"
  | "html_response"
  | "http_error"
  | "timeout"
  | "tls_failure"
  | "too_many_redirects"
  | "connection_failed";

/**
 * One hash's capture: fetched once when the hash was first seen, kept only if
 * its SHA-256 matched.
 */
export interface ScriptCapture {
  state: ScriptCaptureState;
  /** Set on `refused`. A string, so a reason added later still renders. */
  reason: string | null;
  source: ScriptHashSource;
  fetchUrl: string;
  finalUrl: string | null;
  statusCode: number | null;
  /** On `mismatched`: what the origin served instead. */
  receivedHash: string | null;
  sizeBytes: number | null;
  /** The same bytes were already held, for this site or another. */
  reused: boolean;
  attemptedAt: string;
  completedAt: string | null;
}

/** One download of a captured file, from the audit trail. */
export interface ScriptCaptureDownload {
  hash: string;
  downloadedBy: { name: string | null; email: string };
  downloadedAt: string;
}

/** A link to a captured file, good for a minute. */
export interface ScriptCaptureDownloadLink {
  url: string;
  fileName: string;
  sizeBytes: number;
  expiresAt: string;
}

export type ScriptIdentificationState =
  "identified" | "name_mismatch" | "unrecognised" | "unhashed";

/** What can be said about a script's contents (task 356). */
export interface ScriptIdentification {
  state: ScriptIdentificationState;
  library: string | null;
  version: string | null;
  file: string | null;
  source: string | null;
  /** What the URL claims; set only on `name_mismatch`. */
  urlClaim: { name: string; version: string } | null;
}

/** How many of a site's scripts are in each identification state. */
export interface ScriptIdentificationSummary {
  identified: number;
  nameMismatch: number;
  unrecognised: number;
  unhashed: number;
}

/** One published advisory against one library version (task 357). */
export interface LibraryAdvisory {
  id: string;
  /** CVE first. */
  aliases: string[];
  summary: string;
  severity: "critical" | "high" | "medium" | "low" | "unknown";
  /** Null when no fixed release is published. */
  fixedVersion: string | null;
  url: string;
  /** `osv` today. */
  source: string;
  publishedAt: string | null;
  modifiedAt: string;
}

export type ScriptAdvisoryState =
  "affected" | "none_known" | "pending" | "not_assessed" | "not_included";

/** The advisories against the version a script runs now (task 357). */
export interface ScriptAdvisories {
  state: ScriptAdvisoryState;
  /** A severity, or null when there are no advisories. */
  highestSeverity: string | null;
  checkedAt: string | null;
  items: LibraryAdvisory[];
}

/** How many of a site's scripts are in each advisory state. */
export interface ScriptAdvisorySummary {
  affected: number;
  noneKnown: number;
  pending: number;
  notAssessed: number;
}

/** What is stored for one checked library version (task 357). */
export interface LibraryVersionAdvisories {
  library: string;
  version: string;
  checkedAt: string;
  advisories: LibraryAdvisory[];
}

export interface AuthorizedScriptActivity {
  reportCount: number;
  firstSeen: string;
  lastSeen: string;
  pageCount: number;
  paymentPages: Array<{ pageId: string; label: string }>;
  /**
   * Every hash reported in the window, as the browser wrote it; an empty
   * string is a cross-origin load without CORS (task 375).
   */
  hashes: string[];
}

/** One day of the Integrity tab's trend (task 375), in the zone asked for. */
export interface SriCoverageDay {
  /** `YYYY-MM-DD`. */
  date: string;
  /** Distinct scripts reported without integrity metadata; 0 = nothing reported. */
  unprotected: number;
  reportCount: number;
}

/** The policy a site's script decisions make for its payment pages (task 334). */
export interface PaymentPagePolicyResponse {
  policy: PaymentPagePolicy;
}

export interface PaymentPagePolicy {
  /** Where the directives other than `script-src` come from. */
  base: "proposal" | "quoted" | "scan" | "starter";
  /** The policy, without its reporting directives. */
  policy: string;
  reportOnlyHeader: string;
  enforcingHeader: string;
  reportingEndpoints: string;
  scriptSrc: string[];
  scripts: PaymentPolicyScript[];
  warnings: PaymentPolicyWarning[];
  counts: {
    pinned: number;
    anyContent: number;
    unhashable: number;
    rejected: number;
    unreviewed: number;
  };
  /** The payment pages the header is for. */
  paths: Array<{ pageId: string; label: string; matchPattern: string }>;
  /** When the payment pages' reports first quoted it, or null. */
  deployed: { reportOnlySince: string | null; enforcingSince: string | null };
  startDate: string;
  endDate: string;
}

export type PaymentPolicyTreatment =
  | "hash"
  | "path"
  | "path_unhashable"
  | "rejected"
  | "unreviewed"
  | "unrepresentable";

export interface PaymentPolicyScript {
  scriptId: string;
  url: string;
  status: ScriptStatus;
  /** `any_content` or `pinned_hashes` while authorized. */
  integrityMode: string | null;
  treatment: PaymentPolicyTreatment;
  sources: string[];
  integrity: string | null;
  scriptTag: string | null;
  /** Left out here, but the site-wide policy allows its origin. */
  allowedElsewhere: boolean;
  sameOrigin: boolean;
}

export interface PaymentPolicyWarning {
  kind:
    | "unhashable"
    | "unreviewed"
    | "unrepresentable"
    | "strict_dynamic"
    | "unsafe_inline_ignored"
    | "self_allows_rejected"
    | "self_allows_any_content"
    | "nonce"
    | "integrity_on_tags"
    | "older_browsers"
    | "starter_base";
  message: string;
  scriptIds: string[];
}

export interface SriCoverageDailyResponse {
  pageScope: PageScope;
  /** The IANA zone whose calendar days `days` lists (task 453). */
  timeZone: string;
  startDate: string;
  endDate: string;
  days: SriCoverageDay[];
}

/** Whether a browser is integrity-checking a script (task 355). */
export interface ScriptSriCoverage {
  state: "protected" | "unprotected" | "unknown";
  /** `reports` or `scan`; null while the state is `unknown`. */
  source: string | null;
  /** Null unless the state is `unprotected`. */
  lastReportedAt: string | null;
  /** `report` or `enforce`; null unless the state is `unprotected`. */
  disposition: string | null;
}

/** A script a browser reported loading with no integrity metadata. */
export interface UnprotectedScriptReport {
  url: string;
  lastReportedAt: string;
  reportCount: number;
  disposition: "report" | "enforce";
}

/** A site's SRI coverage over the listing's date range (task 355). */
export interface SriCoverageSummary {
  /**
   * The site produced integrity reports in the range. Without it nothing can
   * be called protected, and every count below but `unknownCount` is zero.
   */
  reporting: boolean;
  protectedCount: number;
  unprotectedCount: number;
  unknownCount: number;
  /** Reported unprotected scripts with no inventory row. */
  uninventoried: UnprotectedScriptReport[];
  /** Nothing left to protect: the report-only header can become enforcing. */
  canEnforce: boolean;
  startDate: string;
  endDate: string;
}

/** Compliance › PCI DSS 4.0 (task 372). */
export type PciRequirementStatus = "met" | "partial" | "not_met";

export type PciRequirementId = "6.4.3" | "11.6.1";

export interface PciGap {
  kind:
    | "no_payment_pages"
    | "scripts_not_on_plan"
    | "inventory_truncated"
    | "no_scripts_seen"
    | "unreviewed_scripts"
    | "rejected_scripts_running"
    | "unprotected_scripts"
    | "integrity_unmeasured"
    | "no_recent_scan"
    | "no_rule";
  /** Set for the two script gaps. */
  count: number | null;
  /** Set for `no_rule`. */
  eventType: string | null;
}

export interface PciRequirement643 {
  id: "6.4.3";
  status: PciRequirementStatus;
  facts: {
    paymentPages: number;
    scripts: number;
    authorized: number;
    unreviewed: number;
    rejected: number;
    integrity: {
      protected: number;
      unprotected: number;
      unknown: number;
      measured: boolean;
    };
  };
  gaps: PciGap[];
}

export interface PciRequirement1161 {
  id: "11.6.1";
  status: PciRequirementStatus;
  facts: {
    paymentPages: number;
    scanCadence: "daily" | "none";
    lastScanAt: string | null;
    ruleCoverage: Array<{
      eventType: string;
      label: string;
      rules: Array<{ id: string; name: string }>;
    }>;
    changesDetected30d: number;
    changesAlerted30d: number;
  };
  gaps: PciGap[];
}

export interface PciDssScript {
  id: string;
  url: string;
  status: ScriptStatus;
  integrityMode: string | null;
  justification: string | null;
  decidedBy: { name: string | null; email: string } | null;
  decidedAt: string | null;
  coverage: "protected" | "unprotected" | "unknown";
  stillRunning: boolean;
  paymentPages: Array<{ pageId: string; label: string }>;
}

export interface PciDssReadiness {
  siteId: string;
  siteName: string;
  generatedAt: string;
  requirements: Array<PciRequirement643 | PciRequirement1161>;
  evidence: {
    scripts: PciDssScript[];
    uninventoriedUnprotected: number;
    scriptWindow: { startDate: string; endDate: string } | null;
    pages: Array<{
      id: string;
      label: string;
      scanUrl: string | null;
      lastScanAt: string | null;
      grade: string | null;
      watched: boolean;
    }>;
    changes: Array<{
      scanId: string;
      pageId: string;
      pageLabel: string;
      scannedAt: string;
      headers: string[];
      gradeDroppedFrom: string | null;
      grade: string | null;
      alerted: boolean;
    }>;
  };
  suggestions: Array<{ url: string; suggestedPattern: string }>;
  plan: {
    current: Plan;
    requiredFor: {
      paymentPages: string | null;
      scans: string | null;
      decisions: string | null;
    };
  };
}

/** Which Overview card (task 373). */
export type OverviewCardKey =
  | "newIssues7d"
  | "scriptsAwaitingDecision"
  | "scriptsWithAdvisory"
  | "scriptsUnprotected"
  | "gradeDrops7d"
  | "spikes24h";

/**
 * One Overview count. `count` is null exactly when the plan lacks the card,
 * and `requires` then names the least plan that has it.
 */
export interface OverviewCard {
  count: number | null;
  requires: "none" | "team" | "professional" | "business" | "enterprise";
  href: string;
}

/** One thing to decide, with the dashboard route that opens it. */
export interface OverviewDecision {
  kind: "script" | "issue" | "header" | "policy";
  id: string;
  siteId: string;
  title: string;
  occurredAt: string;
  href: string;
}

/** What the list left out of one kind, and where they are all (task 449). */
export interface OverviewMore {
  kind: OverviewDecision["kind"];
  count: number;
  href: string;
}

/** A stage of the onboarding ladder (task 464), in ladder order. */
export type LadderStage =
  | "install"
  | "discover"
  | "review"
  | "report-only"
  | "enforce"
  | "payment-pages"
  | "payment-policy"
  | "browser"
  | "isolation";

/** Where a site is on the ladder: a stage, or past every one. */
export type OnboardingStage = LadderStage | "settled";

/** The one step the Overview shows for a site, with where its action leads. */
export interface NextStep {
  title: string;
  reason: string;
  evidence: string;
  /** Days of a 7-day wait done, or null. */
  progress: { value: number; of: number } | null;
  action: {
    /** `start-discovery` starts a discovery run before leading to `href`. */
    kind:
      | "start-discovery"
      | "setup"
      | "policy-draft"
      | "issues"
      | "payment-pages"
      | "payment-policy";
    label: string;
    href: string;
    /** Report types to switch on before leading anywhere. */
    enablesTypes: ReportType[];
  };
}

export interface SiteNextStep {
  siteId: string;
  stage: OnboardingStage;
  /** Null once the site is settled. */
  step: NextStep | null;
  stagesDone: LadderStage[];
  stagesSkipped: LadderStage[];
  /** The stages the site said "Not now" to. */
  notNow: LadderStage[];
}

/** Which Content-Security-Policy line a stage's block carries. */
export type StageCspLine =
  | "discovery"
  | "proposal-report-only"
  | "proposal-enforcing"
  | "starter"
  | "own";

export interface SiteNextStepWithHeaders extends SiteNextStep {
  /** The response headers the stage asks for, one line each. */
  headers: string[];
  csp: StageCspLine;
  following: { stage: LadderStage; turnsOn: ReportType[] } | null;
}

/** One line of a header check (task 498). */
export interface HeaderCheckLine {
  /** The block's line. */
  line: string;
  /** The header's name, as the block spells it. */
  name: string;
  result: "served" | "different" | "missing";
  /**
   * Why a `different` line differs (`enforced`, `another-site`,
   * `report-to-undefined` and the rest), else null. A string, as the spec
   * has it: a nullable enum cannot pass API Gateway's import.
   */
  reason: string | null;
  detail: string | null;
  /** What was served under the name, joined; null when nothing was. */
  served: string | null;
}

export interface HeaderCheckFinding {
  kind:
    | "unreachable"
    | "left-site"
    | "status"
    | "other-host"
    | "discovery-enforced"
    | "served-on-redirect";
  message: string;
}

/** "Check my headers" (task 498): what a page served, against the stage's block. */
export interface HeaderCheck {
  /** The URL fetched, before any redirect. */
  url: string;
  checkedAt: string;
  /** The URL that answered, or null when none did. */
  finalUrl: string | null;
  statusCode: number | null;
  stage: OnboardingStage;
  /** Empty when no page was compared. */
  lines: HeaderCheckLine[];
  findings: HeaderCheckFinding[];
}

export interface OverviewResponse {
  siteId: string | null;
  generatedAt: string;
  since: string;
  spikesSince: string;
  cards: Record<OverviewCardKey, OverviewCard>;
  decisions: OverviewDecision[];
  /** Of each kind `decisions` left out, how many and where they are listed. */
  more: OverviewMore[];
  /** Each site's next step on the onboarding ladder, at most five. */
  nextSteps: SiteNextStep[];
  /** For every site in scope, whether no violations are expected yet. */
  siteSilence: SiteSilence[];
}

/**
 * Whether a week with no CSP violations is expected of a site yet (task 502):
 * not before a policy is in its header.
 */
export interface SiteSilence {
  siteId: string;
  /** Where the API read the site in full; absent where it did not. */
  stage?: OnboardingStage;
  /** Null where the API could not place the site past report-only. */
  expectsViolations: boolean | null;
}

/** A script in a site's inventory, with its current decision (task 307). */
export interface AuthorizedScript {
  id: string;
  url: string;
  status: ScriptStatus;
  integrityMode: string | null;
  justification: string | null;
  decidedAt: string | null;
  decidedBy: ScriptDecider | null;
  firstSeenAt: string;
  lastHashSeenAt: string;
  hashes: AuthorizedScriptHash[];
  coverage: ScriptSriCoverage;
  identification: ScriptIdentification;
  advisories: ScriptAdvisories;
  /** Null when it was not reported in the range. */
  activity: AuthorizedScriptActivity | null;
  /**
   * What the daily scans of the site's payment pages found of it in the range
   * (task 333); null when none did.
   */
  scan: AuthorizedScriptScan | null;
}

/** The daily scans that found a script (task 333). */
export interface AuthorizedScriptScan {
  lastScannedAt: string;
  paymentPages: Array<{ pageId: string; label: string }>;
  /**
   * The payment pages whose latest scan in the range no longer finds it
   * (task 358), with the last scan that did. A script gone from every page it
   * was on is listed with these dates rather than dropped.
   */
  missingFrom: Array<{
    pageId: string;
    label: string;
    lastFoundAt: string;
    latestScanAt: string;
  }>;
}

export interface ScriptDecisionRecord {
  id: string;
  status: "authorized" | "rejected";
  integrityMode: string | null;
  justification: string;
  pinnedHashes: string[];
  decidedBy: ScriptDecider;
  decidedAt: string;
}

export interface AuthorizedScriptDetail extends AuthorizedScript {
  /** Every decision, newest first. */
  decisions: ScriptDecisionRecord[];
  /** Every download of a captured file, newest first (task 359). */
  captureDownloads: ScriptCaptureDownload[];
}

export interface SiteScriptsParams {
  pageScope?: PageScope;
  status?: ScriptStatus;
  identification?: ScriptIdentificationState;
  /** Needs `scripts.authorization` (task 357). */
  advisories?: Exclude<ScriptAdvisoryState, "not_included">;
  startDate?: string;
  endDate?: string;
}

export interface SiteScriptsResponse {
  scripts: AuthorizedScript[];
  limit: number;
  pageScope: PageScope;
  startDate: string;
  endDate: string;
  coverage: SriCoverageSummary;
  /** Counted before the `identification` filter narrows the listing. */
  identification: ScriptIdentificationSummary;
  /** Counted before the `advisories` filter narrows the listing. */
  advisories: ScriptAdvisorySummary;
  /** Every checked version among the listed scripts' hashes. */
  libraryAdvisories: LibraryVersionAdvisories[];
}

export interface ScriptDecisionRequest {
  status: "authorized" | "rejected";
  justification: string;
  integrityMode?: ScriptIntegrityMode;
  pinnedHashes?: string[];
}

export interface ScriptPage {
  documentUrl: string;
  scriptCount: number;
  reportCount: number;
  lastSeen: string;
}

export interface ScriptPagesParams {
  siteId: string;
  startDate: string;
  endDate: string;
  limit?: number;
  offset?: number;
  /** `payment`: only the site's payment pages (task 306). */
  pageScope?: PageScope;
}

export interface ScriptPagesResponse {
  pages: ScriptPage[];
}

export type FeedbackCategory = "bug" | "feature" | "question" | "other";

export interface SubmitFeedbackRequest {
  category: FeedbackCategory;
  message: string;
}

export interface DroppedReportSummary {
  /** The host patterns reports are matched against */
  siteHosts: string[];
  /** Size of the summarised window in days */
  days: number;
  /** False when drop counting is not configured — "unknown", not "clean" */
  available: boolean;
  /** Total reports discarded in the window */
  totalDropped: number;
  /** Rejected hosts, most-reported first */
  hosts: Array<{ host: string; count: number }>;
  /** Drops per UTC day, oldest first */
  dailyCounts: Array<{ date: string; count: number }>;
  /** True when totals are a lower bound because the read hit its limit */
  truncated: boolean;
}

/** Reliability (task 314): the range and sites every reliability view reads. */
export interface ReliabilityParams {
  /** One site id, or several comma-separated; omitted means every site. */
  siteId?: string;
  startTime: string;
  endTime: string;
}

export interface NetworkErrorFilters {
  phase?: "dns" | "connection" | "application";
  errorType?: string;
  origin?: string;
}

export interface NetworkErrorGroup {
  errorType: string;
  phase: string;
  origin: string;
  statusCode: number | null;
  elapsedMedianMs: number | null;
  elapsedP95Ms: number | null;
  browsers: string[];
  serverIps: string[];
  protocols: string[];
  methods: string[];
  sampleUrl: string;
  reports: number;
  estimatedReports: number;
  firstSeen: string;
  lastSeen: string;
}

export interface NetworkErrorGroupsResponse {
  items: NetworkErrorGroup[];
  summary: {
    estimatedFailures: number;
    reports: number;
    origins: number;
    groups: number;
  };
}

export interface CrashGroup {
  reason: string;
  page: string;
  isTopLevel: boolean | null;
  visibilityState: string;
  browsers: string[];
  stackExcerpt: string | null;
  reports: number;
  estimatedReports: number;
  firstSeen: string;
  lastSeen: string;
}

export interface CrashGroupsResponse {
  items: CrashGroup[];
  summary: {
    estimatedCrashes: number;
    reports: number;
    pages: number;
    groups: number;
  };
}

export interface ReliabilitySeriesBucket {
  time: string;
  total: number;
  series: Array<{ key: string; estimated: number }>;
}

export interface ReliabilityTotal {
  key: string;
  estimated: number;
  reports: number;
}

export type ReliabilityBucketSize = "1m" | "5m" | "15m" | "1h" | "1d";

export interface NetworkErrorHistogramResponse {
  bucketSize: ReliabilityBucketSize;
  buckets: ReliabilitySeriesBucket[];
  byErrorType: ReliabilityTotal[];
  byPhase: ReliabilityTotal[];
}

export interface CrashHistogramResponse {
  bucketSize: ReliabilityBucketSize;
  buckets: ReliabilitySeriesBucket[];
  byReason: ReliabilityTotal[];
}

function reliabilityQuery(
  params: ReliabilityParams,
  filters: Record<string, string | undefined> = {},
): URLSearchParams {
  const query = new URLSearchParams({
    startTime: params.startTime,
    endTime: params.endTime,
  });
  if (params.siteId) query.set("siteId", params.siteId);
  for (const [name, value] of Object.entries(filters)) {
    if (value) query.set(name, value);
  }
  return query;
}

export const api = {
  auth: {
    /**
     * Get current user profile and trigger provisioning if needed
     * Call this after sign-up/sign-in to ensure user is provisioned
     *
     * @param userInfo - Email and name from Descope SDK (may not be in JWT)
     */
    me: (userInfo?: AuthMeRequest): Promise<AuthMeResponse> =>
      fetchWithAuth("/auth/me", {
        method: "POST",
        body: JSON.stringify(userInfo ?? {}),
      }),
  },

  tenants: {
    /**
     * Name the tenant's organization at onboarding. Repeatable until the
     * tenant has had its first site; a 409 after that (task 508).
     *
     * @param data - Organization name
     */
    setup: (data: TenantSetupRequest): Promise<TenantSetupResponse> =>
      fetchWithAuth("/tenants/setup", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },

  tenant: {
    /**
     * Rename the current tenant's organization.
     *
     * Owner or admin only. Display names need not be unique — what the auth
     * provider holds carries the tenant's own id (task 253) — so there is no
     * duplicate-name conflict to handle. A 400 here names the rule the name
     * broke: its length, or the characters it used.
     *
     * @param name - The new organization display name
     */
    updateName: (name: string): Promise<TenantUpdateResponse> =>
      fetchWithAuth("/tenant", {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),

    /**
     * The tenant's claimed email domains (task 347).
     *
     * Nothing in the product acts on a claim yet; task 348 uses a verified one
     * to decide which users an SSO connection may authenticate.
     */
    domains: {
      list: (): Promise<ListTenantDomainsResponse> =>
        fetchWithAuth("/tenant/domains"),

      /** A 400 names the rule the domain broke in `details.reason`. */
      claim: (domain: string): Promise<{ domain: TenantDomain }> =>
        fetchWithAuth("/tenant/domains", {
          method: "POST",
          body: JSON.stringify({ domain }),
        }),

      /**
       * Run the DNS check now. A check that does not verify is still a 200:
       * read `domain.lastCheckOutcome` and `domain.lastCheckMessage`.
       */
      verify: (domainId: string): Promise<VerifyTenantDomainResponse> =>
        fetchWithAuth(
          `/tenant/domains/${encodeURIComponent(domainId)}/verify`,
          {
            method: "POST",
          },
        ),

      release: (domainId: string): Promise<null> =>
        fetchWithAuth(`/tenant/domains/${encodeURIComponent(domainId)}`, {
          method: "DELETE",
        }),
    },

    /**
     * The tenant's SAML single sign-on connection (task 348).
     *
     * The Business plan and above (task 429): every call answers `403
     * plan_upgrade_required` on any other plan, which is what the settings
     * card renders as its locked state.
     */
    sso: {
      get: (): Promise<TenantSsoResponse> => fetchWithAuth("/tenant/sso"),

      /**
       * Start single sign-on (task 510): gives the tenant the organization
       * the two service-provider values are derived from. Answers 409
       * `no_verified_domain` until a domain is verified.
       */
      setUp: (): Promise<TenantSsoResponse> =>
        fetchWithAuth("/tenant/sso/setup", { method: "POST" }),

      /**
       * Replace the connection. A 400 on `domains` means one of them is not a
       * domain this organisation has verified.
       */
      configure: (
        request: ConfigureTenantSsoRequest,
      ): Promise<TenantSsoResponse> =>
        fetchWithAuth("/tenant/sso", {
          method: "PUT",
          body: JSON.stringify(request),
        }),

      /** Owner only. Removes the connection and forgets its settings. */
      remove: (): Promise<null> =>
        fetchWithAuth("/tenant/sso", { method: "DELETE" }),

      /**
       * Owner only. Require the identity provider for the connection's
       * verified domains, or stop requiring it (task 349).
       *
       * Enabling answers 409 when a precondition is unmet, with `error` set
       * to `no_connection`, `no_verified_domain` or `no_proven_sign_in`.
       */
      setEnforcement: (
        enabled: boolean,
      ): Promise<TenantSsoEnforcementResponse> =>
        fetchWithAuth("/tenant/sso/enforcement", {
          method: "PATCH",
          body: JSON.stringify({ enabled }),
        }),
    },
  },

  /**
   * The tenant's API keys (task 437). Listing is open to every role; issuing
   * and revoking are admin, and issuing needs the Professional plan.
   */
  apiKeys: {
    list: (): Promise<ListApiKeysResponse> => fetchWithAuth("/api-keys"),

    /** The response's `key` is the only time the secret is ever sent. */
    create: (request: CreateApiKeyRequest): Promise<CreatedApiKey> =>
      fetchWithAuth("/api-keys", {
        method: "POST",
        body: JSON.stringify(request),
      }),

    /** Idempotent: revoking a revoked key is still a 204. */
    revoke: (keyId: string): Promise<null> =>
      fetchWithAuth(`/api-keys/${encodeURIComponent(keyId)}`, {
        method: "DELETE",
      }),
  },

  /**
   * The signed-in member's own assistants on the MCP server (task 440).
   * Every role, any plan; each member sees only their own.
   */
  connectedAssistants: {
    list: (): Promise<ListConnectedAssistantsResponse> =>
      fetchWithAuth("/connected-assistants"),

    disconnect: (assistantId: string): Promise<null> =>
      fetchWithAuth(
        `/connected-assistants/${encodeURIComponent(assistantId)}`,
        { method: "DELETE" },
      ),
  },
  sites: {
    list: (): Promise<ListSitesResponse> => fetchWithAuth("/sites"),

    get: (id: string): Promise<{ site: Site }> => fetchWithAuth(`/sites/${id}`),

    create: (data: {
      name: string;
      hosts: string[];
      /** Applied over a new site's settings, which start on the ladder. */
      reportTypes?: Partial<Record<ReportType, SiteReportTypeSettingPatch>>;
    }): Promise<{ site: Site }> =>
      fetchWithAuth("/sites", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    update: (
      id: string,
      data: {
        name?: string;
        isActive?: boolean;
        /** Only the types and fields named change. */
        reportTypes?: Partial<Record<ReportType, SiteReportTypeSettingPatch>>;
        /** Replaces the whole list. */
        hosts?: string[];
      },
    ): Promise<{ site: Site }> =>
      fetchWithAuth(`/sites/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),

    delete: (id: string): Promise<null> =>
      fetchWithAuth(`/sites/${id}`, {
        method: "DELETE",
      }),

    rotateKey: (id: string): Promise<{ ingestCode: string }> =>
      fetchWithAuth(`/sites/${id}/rotate-key`, {
        method: "POST",
      }),

    /** Choose the site's report host label (task 421). */
    setIngestLabel: (id: string, label: string): Promise<IngestLabelResponse> =>
      fetchWithAuth(`/sites/${id}/ingest-label`, {
        method: "PUT",
        body: JSON.stringify({ label }),
      }),

    /** Release it; the code's host keeps working. */
    releaseIngestLabel: (id: string): Promise<null> =>
      fetchWithAuth(`/sites/${id}/ingest-label`, {
        method: "DELETE",
      }),

    /** Report hosts on the customer's own domain (task 422). */
    listHosts: (id: string): Promise<IngestHostListResponse> =>
      fetchWithAuth(`/sites/${id}/hosts`),

    addHost: (id: string, hostname: string): Promise<IngestHost> =>
      fetchWithAuth(`/sites/${id}/hosts`, {
        method: "POST",
        body: JSON.stringify({ hostname }),
      }),

    retryHost: (id: string, hostId: string): Promise<IngestHost> =>
      fetchWithAuth(`/sites/${id}/hosts/${hostId}/retry`, { method: "POST" }),

    removeHost: (id: string, hostId: string): Promise<IngestHost> =>
      fetchWithAuth(`/sites/${id}/hosts/${hostId}`, { method: "DELETE" }),

    getSetup: (id: string): Promise<SetupInstructionsResponse> =>
      fetchWithAuth(`/sites/${id}/setup`),

    /** The site's next step on the onboarding ladder (task 464). */
    getNextStep: (id: string): Promise<SiteNextStepWithHeaders> =>
      fetchWithAuth(`/sites/${id}/next-step`),

    /** "Not now" on a stage, or taking that back. */
    setNotNow: (
      id: string,
      stage: LadderStage,
      notNow: boolean,
    ): Promise<SiteNextStepWithHeaders> =>
      fetchWithAuth(`/sites/${id}/next-step`, {
        method: "PUT",
        body: JSON.stringify({ stage, notNow }),
      }),

    /**
     * "Check my headers" (task 498): fetch one of the site's pages and
     * compare what it served with the stage's block. Without `url`, the
     * site's first host that is not a wildcard.
     */
    checkHeaders: (id: string, url?: string): Promise<HeaderCheck> =>
      fetchWithAuth(`/sites/${id}/header-check`, {
        method: "POST",
        body: JSON.stringify(url ? { url } : {}),
      }),

    /** Policy discovery (task 407): the run, its proposal and decisions. */
    getPolicyDiscovery: (id: string): Promise<PolicyDiscoveryResponse> =>
      fetchWithAuth(`/sites/${id}/policy-discovery`),

    setPolicyDiscovery: (
      id: string,
      body: { enabled: boolean; sampleRate?: number; durationDays?: number },
    ): Promise<PolicyDiscoveryResponse> =>
      fetchWithAuth(`/sites/${id}/policy-discovery`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),

    getPolicyProposal: (id: string): Promise<PolicyProposalResponse> =>
      fetchWithAuth(`/sites/${id}/policy-discovery/proposal`),

    setPolicyDecisions: (
      id: string,
      decisions: Array<{
        directive: string;
        source: string;
        decision: "keep" | "drop" | "clear";
      }>,
    ): Promise<PolicyProposalResponse> =>
      fetchWithAuth(`/sites/${id}/policy-discovery/decisions`, {
        method: "PUT",
        body: JSON.stringify({ decisions }),
      }),

    getDroppedReports: (
      id: string,
      days?: number,
    ): Promise<DroppedReportSummary> =>
      fetchWithAuth(
        `/sites/${id}/dropped-reports${days ? `?days=${days}` : ""}`,
      ),
  },

  reports: {
    list: (
      siteId: string,
      params?: {
        cursor?: string;
        limit?: number;
        directive?: string;
        blockedHost?: string;
        startDate?: string;
        endDate?: string;
      },
    ): Promise<PaginatedResponse<Report>> => {
      const searchParams = new URLSearchParams();
      if (params?.cursor) searchParams.set("cursor", params.cursor);
      if (params?.limit) searchParams.set("limit", params.limit.toString());
      if (params?.directive) searchParams.set("directive", params.directive);
      if (params?.blockedHost)
        searchParams.set("blockedHost", params.blockedHost);
      if (params?.startDate) searchParams.set("startDate", params.startDate);
      if (params?.endDate) searchParams.set("endDate", params.endDate);

      return fetchWithAuth(withQuery(`/sites/${siteId}/reports`, searchParams));
    },

    get: (siteId: string, reportId: string): Promise<{ report: Report }> =>
      fetchWithAuth(`/sites/${siteId}/reports/${reportId}`),
  },

  analytics: {
    getSummary: (siteId: string, range: DateRange): Promise<AnalyticsSummary> =>
      fetchWithAuth(`/sites/${siteId}/analytics/summary?range=${range}`),

    getByDirective: (
      siteId: string,
      range: DateRange,
    ): Promise<{ groups: GroupedCount[] }> =>
      fetchWithAuth(`/sites/${siteId}/analytics/by-directive?range=${range}`),

    getByBlockedHost: (
      siteId: string,
      range: DateRange,
      limit = 20,
    ): Promise<{ groups: GroupedCount[] }> =>
      fetchWithAuth(
        `/sites/${siteId}/analytics/by-blocked-host?range=${range}&limit=${limit}`,
      ),

    getTimeline: (
      siteId: string,
      range: DateRange,
      granularity: Granularity = "day",
    ): Promise<{ dataPoints: TimelineDataPoint[] }> =>
      fetchWithAuth(
        `/sites/${siteId}/analytics/timeline?range=${range}&granularity=${granularity}`,
      ),
  },

  checkout: {
    /**
     * Create a Stripe checkout session for upgrading to a paid plan
     *
     * @param data - Plan and redirect URLs
     * @returns URL to redirect user to Stripe Checkout
     */
    createSession: (
      data: CreateCheckoutSessionRequest,
    ): Promise<CreateCheckoutSessionResponse> =>
      fetchWithAuth("/checkout/session", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    /**
     * Create a setup-mode Checkout session that adds a card to the current
     * subscription, without starting a second one (task 266).
     */
    createPaymentMethodSession: (
      data: CreatePaymentMethodSessionRequest,
    ): Promise<CreateCheckoutSessionResponse> =>
      fetchWithAuth("/checkout/payment-method", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    /**
     * Verify a completed checkout session and ensure subscription is provisioned.
     * Avoids race condition where the user returns before the webhook arrives.
     */
    verify: (sessionId: string): Promise<CheckoutVerifyResponse> =>
      fetchWithAuth("/checkout/verify", {
        method: "POST",
        body: JSON.stringify({ sessionId }),
      }),
  },

  billing: {
    /**
     * Get the current tenant's subscription status
     *
     * @returns Current plan and subscription details
     */
    getSubscription: (): Promise<SubscriptionStatusResponse> =>
      fetchWithAuth("/billing/subscription"),

    /**
     * Create a Stripe Customer Portal session
     */
    createPortalSession: (
      data: CreatePortalSessionRequest,
    ): Promise<PortalSessionResponse> =>
      fetchWithAuth("/billing/portal", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },

  subscription: {
    /**
     * Update subscription plan (for existing paid subscribers)
     * Use this for paid-to-paid plan changes with proper proration
     *
     * @param data - The new plan to switch to
     * @returns Effective date and whether it's a downgrade
     */
    update: (
      data: UpdateSubscriptionRequest,
    ): Promise<UpdateSubscriptionResponse> =>
      fetchWithAuth("/subscription/update", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },

  users: {
    /**
     * List team members
     */
    list: (params?: {
      limit?: number;
      offset?: number;
    }): Promise<ListUsersResponse> => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set("limit", params.limit.toString());
      if (params?.offset) searchParams.set("offset", params.offset.toString());

      return fetchWithAuth(withQuery("/users", searchParams));
    },

    /**
     * Invite a new user to the organization
     */
    invite: (data: InviteUserRequest): Promise<{ user: TeamUser }> =>
      fetchWithAuth("/users/invite", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    /**
     * Update a user's role
     */
    updateRole: (userId: string, role: UserRole): Promise<{ user: TeamUser }> =>
      fetchWithAuth(`/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      }),

    /**
     * Remove a user from the organization
     */
    remove: (userId: string): Promise<null> =>
      fetchWithAuth(`/users/${userId}`, {
        method: "DELETE",
      }),
  },

  profile: {
    /**
     * Get current user profile
     */
    get: (): Promise<GetProfileResponse> => fetchWithAuth("/me"),

    /**
     * Update current user profile
     */
    update: (data: { name?: string }): Promise<{ user: ProfileResponse }> =>
      fetchWithAuth("/me", {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
  },

  explorer: {
    /**
     * List reports with multi-dimensional filtering (Tinybird-powered)
     */
    list: (params: ExplorerListParams = {}): Promise<ExplorerListResponse> => {
      const searchParams = explorerFilterParams(params);
      if (params.limit) searchParams.set("limit", params.limit.toString());
      if (params.offset) searchParams.set("offset", params.offset.toString());

      return fetchWithAuth(withQuery("/reports", searchParams));
    },

    /**
     * Get a single report by ID
     */
    get: (
      reportId: string,
      reportType: ListedReportType = "csp-violation",
    ): Promise<{ report: ExplorerReport | SecurityReport }> =>
      fetchWithAuth(
        withQuery(
          `/reports/${reportId}`,
          new URLSearchParams(
            reportType === "csp-violation" ? {} : { reportType },
          ),
        ),
      ),

    /**
     * The listed types with reports in range, for the type selector (task 312)
     */
    types: (
      filters: Pick<
        ExplorerFilters,
        "siteIds" | "startTime" | "endTime" | "pageScope"
      > & { excludeNoise?: boolean } = {},
    ): Promise<{ types: ReportTypeCount[] }> => {
      const params = explorerFilterParams({
        siteIds: filters.siteIds,
        startTime: filters.startTime,
        endTime: filters.endTime,
        pageScope: filters.pageScope,
      });
      // Issues hides known noise unless asked, so it counts types that way.
      if (filters.excludeNoise) params.set("excludeNoise", "true");
      return fetchWithAuth(withQuery("/reports/types", params));
    },

    /**
     * Get histogram data for timeline visualization
     */
    histogram: (
      filters: ExplorerFilters = {},
      bucketSize?: BucketSize,
    ): Promise<HistogramResponse> => {
      const searchParams = explorerFilterParams(filters);
      if (bucketSize) searchParams.set("bucketSize", bucketSize);

      return fetchWithAuth(withQuery("/reports/histogram", searchParams));
    },

    /**
     * Get facet counts for sidebar filtering
     */
    facets: (filters: ExplorerFilters = {}): Promise<FacetsResponse> =>
      fetchWithAuth(
        withQuery("/reports/facets", explorerFilterParams(filters)),
      ),

    /**
     * Get total count of reports matching filters
     */
    count: (filters: ExplorerFilters = {}): Promise<CountResponse> =>
      fetchWithAuth(withQuery("/reports/count", explorerFilterParams(filters))),

    /**
     * Get aggregated reports grouped by 1-2 dimensions
     */
    grouped: (
      params: GroupedReportsParams,
    ): Promise<GroupedReportsResponse> => {
      const searchParams = explorerFilterParams(params);
      searchParams.set("groupBy", params.groupBy.join(","));
      if (params.limit) searchParams.set("limit", params.limit.toString());

      return fetchWithAuth(withQuery("/reports/grouped", searchParams));
    },
  },

  issues: {
    /**
     * List issues with optional site filtering (query-based route)
     * When siteId is omitted, returns issues across all tenant sites.
     */
    list: (
      params: IssuesListParams & { siteId?: string } = {},
    ): Promise<IssuesListResponse> => {
      const searchParams = new URLSearchParams();
      if (params.siteId) searchParams.set("siteId", params.siteId);
      if (params.reportTypes?.length) {
        searchParams.set("reportType", params.reportTypes.join(","));
      }
      if (params.directive) searchParams.set("directive", params.directive);
      if (params.startTime) searchParams.set("startTime", params.startTime);
      if (params.endTime) searchParams.set("endTime", params.endTime);
      if (params.includeNoise) searchParams.set("includeNoise", "true");
      if (params.pageScope) searchParams.set("pageScope", params.pageScope);
      if (params.state) searchParams.set("state", params.state);
      if (params.firstSeenSince) {
        searchParams.set("firstSeenSince", params.firstSeenSince);
      }
      if (params.limit) searchParams.set("limit", params.limit.toString());
      if (params.offset) searchParams.set("offset", params.offset.toString());

      return fetchWithAuth(withQuery("/issues", searchParams));
    },

    /**
     * Mark an issue expected or fixed, or reopen it (task 448). Member and
     * above; refused in an operator's view and the demo.
     */
    decide: (
      siteId: string,
      signature: string,
      data: IssueDecisionRequest,
    ): Promise<IssueDecisionResponse> =>
      fetchWithAuth(`/sites/${siteId}/issues/${signature}/decision`, {
        method: "POST",
        body: JSON.stringify(data),
      }),

    /** An issue's decisions, newest first (task 448). */
    listDecisions: (
      siteId: string,
      signature: string,
    ): Promise<IssueDecisionsResponse> =>
      fetchWithAuth(`/sites/${siteId}/issues/${signature}/decisions`),

    /**
     * Get noise statistics for a site
     */
    getNoiseStats: (
      siteId: string,
      params: NoiseStatsParams = {},
    ): Promise<NoiseStatsResponse> => {
      const searchParams = new URLSearchParams();
      if (params.startTime) searchParams.set("startTime", params.startTime);
      if (params.endTime) searchParams.set("endTime", params.endTime);

      return fetchWithAuth(
        withQuery(`/sites/${siteId}/noise-stats`, searchParams),
      );
    },

    /**
     * List reports for a specific issue signature
     */
    listReports: (
      siteId: string,
      signature: string,
      reportType: IssueReportType,
      params: IssueReportsListParams = {},
    ): Promise<IssueReportsListResponse> => {
      // The signature and its type together name the issue (task 312).
      const searchParams = new URLSearchParams({ reportType });
      if (params.limit) searchParams.set("limit", params.limit.toString());
      if (params.offset) searchParams.set("offset", params.offset.toString());

      return fetchWithAuth(
        withQuery(`/sites/${siteId}/issues/${signature}/reports`, searchParams),
      );
    },
  },

  usage: {
    /**
     * Get current usage and quota status for the tenant
     */
    get: (): Promise<UsageResponse> => fetchWithAuth("/usage"),
  },

  alerting: {
    listDestinations: (): Promise<ListAlertDestinationsResponse> =>
      fetchWithAuth("/alert-destinations"),

    createDestination: (
      data: CreateAlertDestinationRequest,
    ): Promise<{ destination: AlertDestination }> =>
      fetchWithAuth("/alert-destinations", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    updateDestination: (
      destinationId: string,
      data: UpdateAlertDestinationRequest,
    ): Promise<{ destination: AlertDestination }> =>
      fetchWithAuth(
        `/alert-destinations/${encodeURIComponent(destinationId)}`,
        { method: "PATCH", body: JSON.stringify(data) },
      ),

    /** Rules left with no destination are disabled, and named in the answer. */
    deleteDestination: (
      destinationId: string,
    ): Promise<DeleteAlertDestinationResponse> =>
      fetchWithAuth(
        `/alert-destinations/${encodeURIComponent(destinationId)}`,
        { method: "DELETE" },
      ),

    /**
     * Queue a test notification. The send is asynchronous: its outcome appears
     * in `listDeliveries` as a `test` row carrying the returned `alertId`.
     */
    testDestination: (
      destinationId: string,
    ): Promise<TestAlertDestinationResponse> =>
      fetchWithAuth(
        `/alert-destinations/${encodeURIComponent(destinationId)}/test`,
        { method: "POST" },
      ),

    listRules: (): Promise<ListAlertRulesResponse> =>
      fetchWithAuth("/alert-rules"),

    createRule: (data: CreateAlertRuleRequest): Promise<{ rule: AlertRule }> =>
      fetchWithAuth("/alert-rules", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    updateRule: (
      ruleId: string,
      data: UpdateAlertRuleRequest,
    ): Promise<{ rule: AlertRule }> =>
      fetchWithAuth(`/alert-rules/${encodeURIComponent(ruleId)}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),

    deleteRule: (ruleId: string): Promise<{ deleted: boolean }> =>
      fetchWithAuth(`/alert-rules/${encodeURIComponent(ruleId)}`, {
        method: "DELETE",
      }),

    /** The event types the tenant's plan can route, so none is hard-coded. */
    listEventTypes: (): Promise<ListAlertEventTypesResponse> =>
      fetchWithAuth("/alert-event-types"),

    /** Delivery history, newest first. Pass `nextCursor` back as `cursor`. */
    listDeliveries: (
      params: ListAlertDeliveriesParams = {},
    ): Promise<ListAlertDeliveriesResponse> => {
      const searchParams = new URLSearchParams();
      if (params.limit) searchParams.set("limit", String(params.limit));
      if (params.cursor) searchParams.set("cursor", params.cursor);
      if (params.destinationId)
        searchParams.set("destinationId", params.destinationId);
      if (params.siteId) searchParams.set("siteId", params.siteId);
      if (params.eventType) searchParams.set("eventType", params.eventType);
      if (params.startTime) searchParams.set("startTime", params.startTime);
      return fetchWithAuth(withQuery("/alert-deliveries", searchParams));
    },

    /**
     * The enabled rules that would route each event type on a site. An empty
     * `rules` list means nobody is told about that type there.
     */
    getSiteCoverage: (siteId: string): Promise<SiteAlertCoverageResponse> =>
      fetchWithAuth(`/sites/${encodeURIComponent(siteId)}/alert-coverage`),

    /**
     * Confirm an email address may receive alerts. Public: the person
     * following the link may have no account, so it carries no token and
     * throws `ApiError` with the API's status on failure.
     */
    confirmEmail: async (token: string): Promise<ConfirmAlertEmailResponse> => {
      const response = await fetch(`${API_URL}/alert-email-confirmations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || "Request failed",
          response.status,
          errorData.error,
        );
      }
      return response.json() as Promise<ConfirmAlertEmailResponse>;
    },
  },

  feedback: {
    submit: (data: SubmitFeedbackRequest): Promise<{ success: boolean }> =>
      fetchWithAuth("/feedback", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },

  integrations: {
    slack: {
      /**
       * Get the current Slack integration status
       */
      getStatus: (): Promise<SlackIntegrationStatus> =>
        fetchWithAuth("/integrations/slack"),

      /**
       * Get the Slack OAuth authorization URL
       */
      getAuthorizeUrl: (): Promise<SlackAuthorizeUrlResponse> =>
        fetchWithAuth("/integrations/slack/authorize"),

      /**
       * List available Slack channels, for a Slack alert destination
       * Requires an active Slack integration
       */
      listChannels: (): Promise<SlackChannelsResponse> =>
        fetchWithAuth("/integrations/slack/channels"),

      /**
       * Disconnect the Slack integration
       *
       * Returns `{ success: true }`: unread, but declared.
       */
      disconnect: (): Promise<{ success: boolean }> =>
        fetchWithAuth("/integrations/slack", {
          method: "DELETE",
        }),
    },
  },

  browserChanges: {
    get: (params: BrowserChangesParams): Promise<BrowserChangesResponse> =>
      fetchWithAuth(`/browser-changes?${browserChangesQuery(params)}`),

    getHistogram: (
      params: BrowserChangesParams & { bucketSize?: BucketSize },
    ): Promise<BrowserChangesHistogram> => {
      const query = browserChangesQuery(params);
      if (params.bucketSize) query.set("bucketSize", params.bucketSize);
      return fetchWithAuth(`/browser-changes/histogram?${query}`);
    },
  },

  reliability: {
    networkErrors: (
      params: ReliabilityParams & NetworkErrorFilters,
    ): Promise<NetworkErrorGroupsResponse> =>
      fetchWithAuth(
        withQuery(
          "/reliability/network-errors",
          reliabilityQuery(params, {
            phase: params.phase,
            errorType: params.errorType,
            origin: params.origin,
          }),
        ),
      ),

    networkErrorsHistogram: (
      params: ReliabilityParams &
        NetworkErrorFilters & { bucketSize?: ReliabilityBucketSize },
    ): Promise<NetworkErrorHistogramResponse> =>
      fetchWithAuth(
        withQuery(
          "/reliability/network-errors/histogram",
          reliabilityQuery(params, {
            phase: params.phase,
            errorType: params.errorType,
            origin: params.origin,
            bucketSize: params.bucketSize,
          }),
        ),
      ),

    crashes: (
      params: ReliabilityParams & { reason?: string },
    ): Promise<CrashGroupsResponse> =>
      fetchWithAuth(
        withQuery(
          "/reliability/crashes",
          reliabilityQuery(params, { reason: params.reason }),
        ),
      ),

    crashesHistogram: (
      params: ReliabilityParams & {
        reason?: string;
        bucketSize?: ReliabilityBucketSize;
      },
    ): Promise<CrashHistogramResponse> =>
      fetchWithAuth(
        withQuery(
          "/reliability/crashes/histogram",
          reliabilityQuery(params, {
            reason: params.reason,
            bucketSize: params.bucketSize,
          }),
        ),
      ),
  },

  scripts: {
    getStats: (params: ScriptStatsParams): Promise<ScriptStats> => {
      const searchParams = new URLSearchParams({
        siteId: params.siteId,
        startDate: params.startDate,
        endDate: params.endDate,
      });
      if (params.pageScope) searchParams.set("pageScope", params.pageScope);
      return fetchWithAuth(`/scripts/stats?${searchParams}`);
    },

    getOrigins: (
      params: ScriptOriginsParams,
    ): Promise<ScriptOriginsResponse> => {
      const searchParams = new URLSearchParams({
        siteId: params.siteId,
        startDate: params.startDate,
        endDate: params.endDate,
      });
      if (params.pageScope) searchParams.set("pageScope", params.pageScope);
      if (params.limit) searchParams.set("limit", String(params.limit));
      if (params.offset) searchParams.set("offset", String(params.offset));
      return fetchWithAuth(`/scripts/origins?${searchParams}`);
    },

    getInventory: (
      params: ScriptInventoryParams,
    ): Promise<ScriptInventoryResponse> => {
      const searchParams = new URLSearchParams({
        siteId: params.siteId,
        startDate: params.startDate,
        endDate: params.endDate,
      });
      if (params.pageScope) searchParams.set("pageScope", params.pageScope);
      if (params.origin) searchParams.set("origin", params.origin);
      if (params.limit) searchParams.set("limit", String(params.limit));
      if (params.offset) searchParams.set("offset", String(params.offset));
      return fetchWithAuth(`/scripts/inventory?${searchParams}`);
    },

    getPages: (params: ScriptPagesParams): Promise<ScriptPagesResponse> => {
      const searchParams = new URLSearchParams({
        siteId: params.siteId,
        startDate: params.startDate,
        endDate: params.endDate,
      });
      if (params.pageScope) searchParams.set("pageScope", params.pageScope);
      if (params.limit) searchParams.set("limit", String(params.limit));
      if (params.offset) searchParams.set("offset", String(params.offset));
      return fetchWithAuth(`/scripts/pages?${searchParams}`);
    },
  },

  /** Script authorization (task 307). */
  siteScripts: {
    list: (
      siteId: string,
      params: SiteScriptsParams = {},
    ): Promise<SiteScriptsResponse> => {
      const searchParams = new URLSearchParams();
      if (params.pageScope) searchParams.set("pageScope", params.pageScope);
      if (params.status) searchParams.set("status", params.status);
      if (params.identification) {
        searchParams.set("identification", params.identification);
      }
      if (params.advisories) searchParams.set("advisories", params.advisories);
      if (params.startDate) searchParams.set("startDate", params.startDate);
      if (params.endDate) searchParams.set("endDate", params.endDate);
      const query = searchParams.toString();
      return fetchWithAuth(
        `/sites/${siteId}/scripts${query ? `?${query}` : ""}`,
      );
    },

    get: (
      siteId: string,
      scriptId: string,
    ): Promise<{ script: AuthorizedScriptDetail }> =>
      fetchWithAuth(`/sites/${siteId}/scripts/${scriptId}`),

    decide: (
      siteId: string,
      scriptId: string,
      data: ScriptDecisionRequest,
    ): Promise<{ decision: ScriptDecisionRecord }> =>
      fetchWithAuth(`/sites/${siteId}/scripts/${scriptId}/decision`, {
        method: "POST",
        body: JSON.stringify(data),
      }),

    /**
     * A link to the captured file of one hash (task 359). Owner and admin;
     * recorded in the audit trail before the link is issued.
     */
    downloadCapture: (
      siteId: string,
      scriptId: string,
      hash: string,
    ): Promise<ScriptCaptureDownloadLink> =>
      fetchWithAuth(`/sites/${siteId}/scripts/${scriptId}/capture/download`, {
        method: "POST",
        body: JSON.stringify({ hash }),
      }),

    /** The policy the decisions make for the payment pages (task 334). */
    paymentPolicy: (siteId: string): Promise<PaymentPagePolicyResponse> =>
      fetchWithAuth(`/sites/${siteId}/scripts/payment-policy`),

    /** Unprotected scripts per day (task 375), every day listed. */
    sriCoverageDaily: (
      siteId: string,
      params: {
        pageScope?: PageScope;
        startDate?: string;
        endDate?: string;
        timeZone?: string;
      },
    ): Promise<SriCoverageDailyResponse> => {
      const searchParams = new URLSearchParams();
      if (params.pageScope) searchParams.set("pageScope", params.pageScope);
      if (params.startDate) searchParams.set("startDate", params.startDate);
      if (params.endDate) searchParams.set("endDate", params.endDate);
      if (params.timeZone) searchParams.set("timeZone", params.timeZone);
      const query = searchParams.toString();
      return fetchWithAuth(
        `/sites/${siteId}/scripts/sri-coverage/daily${query ? `?${query}` : ""}`,
      );
    },

    /** The export as CSV text, built by the API. */
    exportCsv: (siteId: string, pageScope: PageScope): Promise<string> =>
      fetchTextWithAuth(
        `/sites/${siteId}/scripts/inventory.csv?pageScope=${pageScope}`,
      ),
  },

  destinations: {
    list: (params: DestinationsParams): Promise<DestinationsResponse> => {
      const searchParams = new URLSearchParams({
        startDate: params.startDate,
        endDate: params.endDate,
      });
      if (params.siteId) searchParams.set("siteId", params.siteId);
      if (params.pageScope) searchParams.set("pageScope", params.pageScope);
      return fetchWithAuth(`/destinations?${searchParams}`);
    },
  },

  /** Policy › Frames (task 367): the same parameters as destinations. */
  frames: {
    list: (params: DestinationsParams): Promise<FramedOriginsResponse> => {
      const searchParams = new URLSearchParams({
        startDate: params.startDate,
        endDate: params.endDate,
      });
      if (params.siteId) searchParams.set("siteId", params.siteId);
      if (params.pageScope) searchParams.set("pageScope", params.pageScope);
      return fetchWithAuth(`/frames?${searchParams}`);
    },
  },

  /** The Overview (task 373): one site, or every site when none. */
  overview: {
    get: (siteId?: string): Promise<OverviewResponse> =>
      fetchWithAuth(
        siteId === undefined
          ? "/overview"
          : `/overview?${new URLSearchParams({ siteId })}`,
      ),
  },

  /** Compliance › PCI DSS 4.0 (task 372): read-only, every plan. */
  compliance: {
    pciDss: (siteId: string): Promise<PciDssReadiness> =>
      fetchWithAuth(`/compliance/pci-dss?${new URLSearchParams({ siteId })}`),

    /** One requirement's evidence as CSV text, built by the API. */
    exportPciDssEvidence: (
      siteId: string,
      requirement: PciRequirementId,
    ): Promise<string> =>
      fetchTextWithAuth(
        `/compliance/pci-dss/evidence.csv?${new URLSearchParams({ siteId, requirement })}`,
      ),
  },

  pages: {
    list: (siteId: string): Promise<ListMonitoredPagesResponse> =>
      fetchWithAuth(`/sites/${siteId}/pages`),

    create: (
      siteId: string,
      data: CreateMonitoredPageRequest,
    ): Promise<{ page: MonitoredPage }> =>
      fetchWithAuth(`/sites/${siteId}/pages`, {
        method: "POST",
        body: JSON.stringify(data),
      }),

    update: (
      siteId: string,
      pageId: string,
      data: UpdateMonitoredPageRequest,
    ): Promise<{ page: MonitoredPage }> =>
      fetchWithAuth(`/sites/${siteId}/pages/${pageId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),

    delete: (siteId: string, pageId: string): Promise<null> =>
      fetchWithAuth(`/sites/${siteId}/pages/${pageId}`, { method: "DELETE" }),

    suggestions: (siteId: string): Promise<PageSuggestionsResponse> =>
      fetchWithAuth(`/sites/${siteId}/page-suggestions`),

    /** A monitored page's scheduled scans, newest first (task 192). */
    scans: (
      siteId: string,
      pageId: string,
      params: { limit?: number; before?: string } = {},
    ): Promise<ListPageScansResponse> => {
      const searchParams = new URLSearchParams();
      if (params.limit) searchParams.set("limit", String(params.limit));
      if (params.before) searchParams.set("before", params.before);
      const query = searchParams.toString();
      return fetchWithAuth(
        `/sites/${siteId}/pages/${pageId}/scans${query ? `?${query}` : ""}`,
      );
    },

    /**
     * Every scan on the site's monitored pages that recorded a change, newest
     * first (task 374). `limit: 0` asks for the count alone.
     */
    changes: (
      siteId: string,
      params: {
        limit?: number;
        cursor?: string;
        startTime?: string;
        endTime?: string;
        /** Only the scans that dropped the page's grade (task 449). */
        gradeDropped?: boolean;
      } = {},
    ): Promise<ListPageChangesResponse> => {
      const searchParams = new URLSearchParams();
      if (params.limit !== undefined) {
        searchParams.set("limit", String(params.limit));
      }
      if (params.cursor) searchParams.set("cursor", params.cursor);
      if (params.startTime) searchParams.set("startTime", params.startTime);
      if (params.endTime) searchParams.set("endTime", params.endTime);
      if (params.gradeDropped) searchParams.set("gradeDropped", "true");
      const query = searchParams.toString();
      return fetchWithAuth(
        `/sites/${siteId}/page-changes${query ? `?${query}` : ""}`,
      );
    },

    scan: (
      siteId: string,
      pageId: string,
      scanId: string,
    ): Promise<{ scan: PageScan }> =>
      fetchWithAuth(`/sites/${siteId}/pages/${pageId}/scans/${scanId}`),

    /** The policies a site's reports quoted, optionally from one page. */
    cspPolicies: (
      siteId: string,
      params: { pageId?: string } = {},
    ): Promise<ListCspPoliciesResponse> => {
      const query = params.pageId
        ? `?${new URLSearchParams({ pageId: params.pageId })}`
        : "";
      return fetchWithAuth(`/sites/${siteId}/csp-policies${query}`);
    },
  },
};

export { ApiError };
